/**
 * Simplified Chatbot Store with Zustand
 * Single source of truth for chatbot state
 */

import { create, type StateCreator } from 'zustand';
import { createStore } from 'zustand/vanilla';
import { createJSONStorage, persist, subscribeWithSelector } from 'zustand/middleware';
import {
  ChatbotStore,
  ChatbotStoreState,
  ChatbotState,
  ChatbotView,
  MessageType,
  MessageStatus,
  ResponseStage,
  ChatbotMessage,
  ResponseProgress,
  ChatbotError,
  ChatbotSettings,
  ConversationHistoryItem,
  UploadedImage,
  ContinuationState,
  StreamInterruptedState,
  SSEConnectionState,
  InterruptAction,
  ExecutionTodo,
  InterruptionDelivery,
  SessionExpiryDelivery,
  SessionRecovery,
  TurnPending,
} from './types';
import posthog from '@libs/posthog';
import { EVENTS } from '@lib/analytics';
import chatbotApi, { createDeliveryAttempt, withDeliveryAttempt, type DeliveryAttempt } from '@services/chatbot/chatbotApi';
import { claimAutoResume, deliveryErrorContext } from '@services/chatbot/deliveryRecovery';
import { mintSendId } from '@services/chatbot/pageExitDelivery';
import { attachAuthHeaders } from '@apis/authHeaders';
import { currentUserPin } from '@services/chatbot/tabIdentity';
import {
  imagesSurviveReload,
  parkChatSend,
  parkedSendTarget,
  claimParkedSend,
  readParkedChatSend,
  startsConversation,
  takeParkedChatSend,
} from './parkedChatSend';
import NewConversationService from '@apis/NewConversationService';
import { parseAgentFinalContent, parseSuggestedResponses } from '@utils/parseSuggestedResponses';
import { continuationRecoveryContext, crossesTurnBoundary, mergeSnapshotWithLiveTail, upsertTurnReply, type ToolMarker } from './liveTurn';
import {
  acceptInboxItem,
  dropInboxItem,
  dropSavedInboxItems,
  dropSavedExecutiveInbox,
  foldExecutiveSteers,
  mergeExecutiveHistory,
  foldReadInboxItems,
  markInboxRead,
  mergeServerPending,
  planTurnEnd,
  queueExecutiveLeftovers,
  hasPendingExecutiveInbox,
  prepareExecutiveSend,
  acknowledgeExecutiveSend,
  retainUncertainExecutiveSend,
  queueInboxItem,
  type InboxItem,
} from './midTurnInbox';
import { captureExecutiveSession } from './executiveSession';
import { executiveSendRefusal, isExecutiveConversationId } from '@apis/executiveContract';
import { toast } from 'react-toastify';
import { queryClient } from '../../libs/queryClient';
import { chatHistoryQueryKeys, updateChatHistoryCache } from '@apis/queries/chatHistoryCache';
import { extractView } from '@components/AdsPanel/markup';
import {
  dataQueryKeys,
  executiveOverviewQueryKeys,
  feedQueryKeys,
  leadAttributionQueryKeys,
  monitorQueryKeys,
} from '@apis/queries/tenantQueryKeys';
import { getAppStorePersistKey } from '@stores/utils';
import {
  setChatContext,
  clearChatContext,
  addChatBreadcrumb,
  traceChatTurn,
  recordFirstToken,
  recordStreamComplete,
  captureChatWarning,
  captureChatError,
} from '../../libs/sentryChat';
import { normalizeExecutionTodo } from '@services/chatbot/executionTodoState';
import {
  getSharedConversationStore,
  forEachSharedConversationStore,
} from './sharedConversationStores';
import { deriveConversationTitle, savedPresentation } from './messagePresentation';
import { isSessionExpiredError } from './sendAuthRecovery';
import { chatMessageLengthError } from '@services/chatbot/buildMessageWithAttachments';
import WorkspaceContextService from '@services/WorkspaceContextService';

// ── Conversation tracking state for PostHog analytics ──────────────
let _conversationStartedAt: number | null = null;
let _conversationMessageCount = 0;

/** Staff steering the Executive tab is not customer usage: its events say so. */
const analyticsSurface = (conversationId?: string | null) =>
  conversationId?.startsWith('exec__') ? { surface: 'executive' } : {};

function _resetConversationTracking() {
  _conversationStartedAt = null;
  _conversationMessageCount = 0;
}

function _captureConversationEnded(conversationId: string | undefined) {
  if (!conversationId || !_conversationStartedAt) return;
  const durationMs = Date.now() - _conversationStartedAt;
  posthog.capture(EVENTS.CONVERSATION.ENDED, {
    ...analyticsSurface(conversationId),
    conversation_id: conversationId,
    message_count: _conversationMessageCount,
    duration_seconds: Math.round(durationMs / 1000),
  });
  _resetConversationTracking();
}

// Default settings
const STORE_NAME = 'chatbot';

const defaultSettings: ChatbotSettings = {
  mockMode: false, // Use real API by default
};

// Timeouts for retry vs resume decision (in milliseconds)
const SSE_SILENT_RETRY_TIMEOUT = 15000; // 15 seconds - show reconnect toast

/** The server saves a reply's <view name="analytics"> when it stores the reply (data_views.capture); every
 *  path a reply reaches the client by — live, re-attached, reloaded — refreshes the Analytics dashboard's
 *  charts when it carries one. */
const refreshAnalyticsIfViewed = (text: string) => {
  if (extractView(text, 'analytics')) void queryClient.invalidateQueries({ queryKey: dataQueryKeys.all });
};
const SSE_RECONNECT_TIMEOUT = 30000; // 30 seconds - show Resume banner
const SSE_WORKER_TIMEOUT = 3600000; // 1 hour - worker has timed out (resume still allowed)

let phaseUpdateTimeout: ReturnType<typeof setTimeout> | null = null;

// Initial continuation state
const initialContinuationState: ContinuationState = {
  isRecovering: false,
  originalConversationId: null,
  retryMessage: null,
  recoveryStartTime: null,
};

// Initial stream interrupted state
const initialStreamInterruptedState: StreamInterruptedState = {
  isInterrupted: false,
  partialResponse: '',
  error: '',
  isRetryable: true,
  originalMessage: '',
  uploadedImages: [],
  interruptedAt: null,
  conversationId: null,
  source: 'unknown',
  phase: 'idle',
  disconnectStartedAt: null,
  lastReconnectAttemptAt: null,
};

// Initial SSE connection state
const initialSSEConnectionState: SSEConnectionState = {
  isConnected: true,
  disconnectedAt: null,
  lastEventId: 0,
  reconnectAttempts: 0,
};

// Helper to normalize chatbot state on hydration (convert transient states to stable)
const normalizeChatbotState = (state: ChatbotState | undefined): ChatbotState => {
  if (!state || !Object.values(ChatbotState).includes(state)) {
    return ChatbotState.COLLAPSED; // Default to COLLAPSED if invalid or missing
  }
  // Convert transient states to stable states on page load
  if (state === ChatbotState.OPENING) {
    return ChatbotState.OPEN;
  }
  if (state === ChatbotState.CLOSING) {
    return ChatbotState.COLLAPSED;
  }
  return state;
};


// Initial state
// NOTE: Zustand persist middleware hydrates 'state' and 'conversationId' per workspace (key uses getAppStorePersistKey so each workspace has its own persisted state).
// The component must call initializeStore() on mount to load the conversation messages when conversationId is set.
const initialState: ChatbotStoreState = {
  // Core data
  messages: [],
  inbox: [],
  executiveSteer: null,
  executiveNotice: null,
  executiveHistoryNextCursor: null,
  executiveHistoryViewingOlder: false,
  executiveHistoryLoading: false,
  executiveHistoryLoadId: 0,
  conversationId: undefined,
  title: null,

  // UI state
  state: ChatbotState.COLLAPSED, // Default closed; hydrated from persisted state for returning users
  panelStretched: false,
  responseStage: ResponseStage.IDLE,
  answerCompletedAt: null,
  answerPending: null,
  inboxResending: false,
  currentView: ChatbotView.CHAT,
  activeDetailRef: null,
  inputValue: '',
  inputFocusRequestId: 0,
  inputFocused: false,
  inputPlaceholderOverride: null,
  artifactPickerSection: null,
  artifactPickerRestrictTo: null,
  showWelcomeScreen: true, // Will be updated based on conversationId after hydration

  // Response tracking
  currentResponse: null,
  activeTools: [],

  // Message capacity tracking
  messageCapacity: null,
  executionTodo: null,

  // Continuation state for context overflow recovery
  continuation: initialContinuationState,

  // Stream interrupted state for retry/resume
  streamInterrupted: initialStreamInterruptedState,

  // SSE connection state
  sseConnection: initialSSEConnectionState,

  // Chat history
  conversationHistory: [],
  historyLoading: false,
  historySearchText: '',
  historyOriginFilter: 'all',
  historyStartedByFilter: 'all',
  conversationLoading: false, // Will be updated based on conversationId after hydration

  // Error handling
  error: null,

  // Settings
  settings: defaultSettings,

  // Side modal state
  isAssetHubModalOpen: false,

  // Pending artifacts (queued from outside ChatbotContext, consumed on render)
  pendingArtifacts: [],

  // Slack simulation default — off. Persisted across reloads so an
  // internal user who flipped it on yesterday is still in Slack-sim
  // mode today (they can toggle off when they're done).
  slackSimulation: { enabled: false, async: false },

  // Per-account Code Mode opt-in (from /me code_mode_enabled). NOT persisted — it's
  // re-synced from the auth context each session, so disabling the account takes
  // effect on the next load (fail-closed default false).
  codeModeEnabled: false,

  // Per-account Code Mode SIMPLE opt-in (from /me code_mode_simple_enabled). NOT
  // persisted, same as codeModeEnabled. Fail-closed default false.
  codeModeSimpleEnabled: false,

  // Read-only ask-card marker. Only a per-card instance store (created via
  // sharedConversationStores for a conv_memo_ reply) sets this true; the global
  // panel store leaves it false so main-chat sends — including a "Continue in chat"
  // continuation of an ask-card conversation — run full Leo. NOT persisted.
  askCardReadOnly: false,
};

// Persist state and conversationId per workspace (getAppStorePersistKey includes
// workspace, so switching workspaces restores that workspace's conversation).
// localStorage, deliberately: sessionStorage here (a #6511 experiment) made any
// non-F5 reload lose the panel's state and auto-start a new chat. Persist never
// re-reads mid-session, so parallel tabs can't disturb each other's live state
// through this key.
const chatbotStoreStorage = createJSONStorage<Pick<
  ChatbotStoreState,
  | 'state'
  | 'conversationId'
  | 'slackSimulation'
  | 'panelStretched'
>>(() => ({
  getItem: (_name: string) => {
    try {
      return localStorage.getItem(getAppStorePersistKey(STORE_NAME));
    } catch {
      return null;
    }
  },
  setItem: (_name: string, value: string) => {
    try {
      localStorage.setItem(getAppStorePersistKey(STORE_NAME), value);
    } catch {
      // no-op
    }
  },
  removeItem: (_name: string) => {
    try {
      localStorage.removeItem(getAppStorePersistKey(STORE_NAME));
    } catch {
      // no-op
    }
  },
}));


const clearInterruptionTimers = () => {
  if (phaseUpdateTimeout) {
    clearTimeout(phaseUpdateTimeout);
    phaseUpdateTimeout = null;
  }
};

const assertInterruptionState = (state: StreamInterruptedState, context: string) => {
  if (state.phase === 'blocked' && state.isRetryable) {
    console.warn('⚠️ [chatbotStore] Interrupted state mismatch: blocked but retryable', {
      context,
      phase: state.phase,
      isRetryable: state.isRetryable,
      error: state.error,
    });
  }
  if (state.phase === 'retry' && !state.isRetryable) {
    console.warn('⚠️ [chatbotStore] Interrupted state mismatch: retry but non-retriable', {
      context,
      phase: state.phase,
      isRetryable: state.isRetryable,
      error: state.error,
    });
  }
};

/** Who this tab is acting as — a parked message replays only into the same person and tenant. */
const currentTenantPins = (): { workspaceId?: string; masqueradeId?: string; userId?: string } => {
  try {
    const headers = attachAuthHeaders({});
    return {
      workspaceId: headers['X-Workspace-Id'],
      masqueradeId: headers.Masquerade,
      userId: currentUserPin(),
    };
  } catch {
    return {};
  }
};

const SESSION_RECOVERY_TOAST: Record<SessionRecovery, string> = {
  replay: 'Your session expired. Reconnect and your message will send.',
  restore: 'Your session expired. Reconnect and your message will be back in the box.',
  reattach: "Your session expired. Reconnect to pick up Leo's answer.",
};

const normalizeUploadedImages = (images?: UploadedImage[]): UploadedImage[] =>
  (images || []).map((image) => ({
    ...image,
    name: image.name || image.file?.name,
    file: null,
  }));

export const deriveInterruptAction = (streamInterrupted: StreamInterruptedState): InterruptAction => {
  if (!streamInterrupted.isInterrupted) return 'none';
  if (streamInterrupted.phase === 'session_expired') return 'refresh';
  if (streamInterrupted.source === 'network' && streamInterrupted.phase === 'resume') {
    return 'resume';
  }
  if (streamInterrupted.isRetryable) {
    return 'retry';
  }
  return 'none';
};

const getNetworkPhaseForElapsed = (elapsedMs: number): StreamInterruptedState['phase'] => {
  if (elapsedMs >= SSE_RECONNECT_TIMEOUT) {
    return 'resume';
  }
  if (elapsedMs >= SSE_SILENT_RETRY_TIMEOUT) {
    return 'toast';
  }
  return 'silent';
};

const scheduleNetworkPhaseUpdate = (
  get: () => ChatbotStore,
  set: (partial: Partial<ChatbotStoreState>) => void,
  disconnectStartedAt: number
) => {
  const tick = () => {
    const currentState = get();
    const { sseConnection, streamInterrupted } = currentState;

    if (sseConnection.isConnected || streamInterrupted.source !== 'network') {
      return;
    }
    if (streamInterrupted.disconnectStartedAt !== disconnectStartedAt) {
      return;
    }

    const elapsedMs = Date.now() - disconnectStartedAt;
    const nextPhase = getNetworkPhaseForElapsed(elapsedMs);

    if (nextPhase === 'toast' && streamInterrupted.phase !== 'toast') {
      toast.info('Reconnecting…', {
        autoClose: 5000,
        toastId: 'connection-lost',
      });
      set({
        streamInterrupted: {
          ...streamInterrupted,
          phase: 'toast',
        },
      });
    }

    if (nextPhase === 'resume' && streamInterrupted.phase !== 'resume') {
      const isWorkerTimeout = elapsedMs >= SSE_WORKER_TIMEOUT;
      const resumeError = isWorkerTimeout
        ? 'Connection lost - worker may have timed out'
        : 'Connection lost - network issue';

      console.log(`⚠️ [chatbotStore] ${isWorkerTimeout ? 'Worker' : 'Network'} timeout - showing Resume`);
      toast.warning('Connection lost. Click Resume to reconnect.', {
        autoClose: false,
        toastId: 'connection-lost',
      });

      set({
        streamInterrupted: {
          ...streamInterrupted,
          isInterrupted: true,
          error: resumeError,
          phase: 'resume',
          interruptedAt: disconnectStartedAt,
        },
        responseStage: ResponseStage.IDLE,
        currentResponse: null,
      });
      return;
    }

    if (nextPhase === 'resume') {
      return;
    }

    if (nextPhase === 'silent' && streamInterrupted.phase !== 'silent') {
      set({
        streamInterrupted: {
          ...streamInterrupted,
          phase: 'silent',
        },
      });
    }

    const nextBoundary =
      nextPhase === 'silent' ? SSE_SILENT_RETRY_TIMEOUT : SSE_RECONNECT_TIMEOUT;
    const delayMs = Math.max(0, nextBoundary - elapsedMs);
    phaseUpdateTimeout = setTimeout(tick, delayMs);
  };

  clearInterruptionTimers();
  phaseUpdateTimeout = setTimeout(tick, 0);
};

// Utility functions
const generateId = () => `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// Longer than the 100ms idle settle a finished turn schedules, so the next
// message a turn end sends on is not reset to idle by the turn before it.
const TURN_SETTLE_MS = 150;

const inboxItemToMessage = (item: InboxItem): ChatbotMessage => ({
  id: generateId(),
  type: MessageType.USER,
  content: item.message,
  timestamp: Date.now(),
  status: MessageStatus.SENT,
  inboxStatus: 'read',
});

const createMessage = (
  type: MessageType,
  content: string | any,
  status: MessageStatus = MessageStatus.SENT,
  images?: UploadedImage[],
  hidden?: boolean,
  display?: string
): ChatbotMessage => ({
  id: generateId(),
  type,
  content,
  timestamp: Date.now(),
  status,
  images,
  hidden,
  display,
});

// Capacity is now calculated by the backend and stored in S3 metadata
// Frontend only displays capacity, never calculates it
// Old conversations without metadata.capacity will show 0% until first message exchange

// Shared state + actions initializer. The global singleton (`useChatbotStore`,
// below) wraps it in `persist`; `createChatbotStore()` factory instances wrap the
// SAME creator without persist — so an agentic view can spin up an isolated
// conversation store that reuses the exact streaming / approval / tool-marker
// mechanics of the main chat instead of rebuilding them. No action references
// `useChatbotStore` directly (only `get()`/`set()`), so factory instances stay
// fully isolated from the global store.
// Single-flight guard for initializeStore, keyed by conversation id. The
// reconciler has several legitimate entry points (panel mount, chat switch,
// the open-chats rehydrate listener, syncStreamLifecycle's self-hydration) and
// they can all fire within the same tick — without this, concurrent
// loadConversationById runs interleave with a live attach and clobber the
// streaming bubble it is building. Module scope is correct: conversation ids
// are globally unique across store instances.
const initializeInFlight = new Map<string, Promise<void>>();

/** Run `fn` once per key: concurrent callers await the same in-flight run. */
function singleFlight(
  inFlight: Map<string, Promise<void>>,
  key: string | undefined,
  fn: () => Promise<void>,
): Promise<void> {
  if (!key) return fn();
  const existing = inFlight.get(key);
  if (existing) return existing;
  const run = fn().finally(() => inFlight.delete(key));
  inFlight.set(key, run);
  return run;
}

const chatbotStateCreator: StateCreator<
  ChatbotStore,
  [['zustand/subscribeWithSelector', never]],
  [],
  ChatbotStore
> = (set, get) => ({
      ...initialState,

      // Lifecycle actions
      openChatbot: (options?) => {
        // Session-level: this action ALWAYS runs on the global store — the
        // panel's open/closed state is a global UI toggle, not a per-chat
        // field. When a prefill or initial-message is requested, the
        // conversation-level payload is routed to a FRESH chat via the
        // openChatsStore coordinator so external "Ask Leo about X" buttons
        // reliably start a new tab instead of appending to whichever chat
        // was last active.
        const { initialMessage, hideInitialMessage, displayText, prefillInput, prefillArtifacts, seedUserMessage } = options || {};
        const hasAutoMessage = !!initialMessage;
        const hasPrefill = !!prefillInput || (prefillArtifacts && prefillArtifacts.length > 0);
        const hasSeedMessage = !!seedUserMessage;

        const routeToNewChat = hasAutoMessage || hasPrefill || hasSeedMessage;

        // Dynamic import to sidestep the module-load cycle (openChatsStore
        // reaches back to `createChatbotStore` via sharedConversationStores).
        // Evaluated only at action-invocation time.
        const openChatsPromise = routeToNewChat
          ? import('./openChatsStore').then((mod) => {
              const openChats = mod.useOpenChatsStore.getState();
              // Auto-message guard: don't double-fire if the ACTIVE per-chat
              // store is already mid-response.
              if (hasAutoMessage && openChats.activeConversationId) {
                const activeStore = getSharedConversationStore(openChats.activeConversationId);
                if (activeStore.getState().responseStage !== ResponseStage.IDLE) {
                  return null;
                }
              }
              return openChats.startNewChat();
            })
          : Promise.resolve(null);

        set({ state: ChatbotState.OPENING });

        setTimeout(() => {
          set({ state: ChatbotState.OPEN });

          openChatsPromise.then((newCid) => {
            if (!newCid) return;
            const store = getSharedConversationStore(newCid).getState();
            if (hasPrefill) {
              store.setInputValue(prefillInput || '');
              if (prefillArtifacts && prefillArtifacts.length > 0) {
                // pendingArtifacts is a plain state field, no dedicated action
                getSharedConversationStore(newCid).setState({
                  pendingArtifacts: prefillArtifacts,
                });
              }
              store.requestInputFocus();
            }
            if (hasSeedMessage) {
              // Display-only: render the card on the user side without sending.
              // The card's Run button fires the real (grounded) turn later.
              store.addMessage({
                type: MessageType.USER,
                content: seedUserMessage!,
                status: MessageStatus.SENT,
              });
              getSharedConversationStore(newCid).setState({ showWelcomeScreen: false });
            }
            if (hasAutoMessage) {
              setTimeout(() => {
                store.sendMessage(initialMessage!, undefined, hideInitialMessage, displayText);
              }, 300);
            }
          });
        }, 300);
      },

      collapseChat: () => {
        set({ state: ChatbotState.COLLAPSED });
      },

      togglePanelStretched: () => {
        set((s) => ({ panelStretched: !s.panelStretched }));
      },

      setPanelStretched: (panelStretched: boolean) => {
        if (get().panelStretched === panelStretched) return;
        set({ panelStretched });
      },

      // Messaging actions
      sendMessage: async (content: string, images?: UploadedImage[], hidden?: boolean, display?: string, onAccepted?: () => void, sendId?: string) => {
        if (!content.trim() && (!images || images.length === 0)) return;

        // Validate message length to prevent context overflow
        const lengthError = chatMessageLengthError(content, images);
        if (lengthError) {
          set({
            error: {
              id: generateId(),
              code: 'MESSAGE_TOO_LONG',
              message: lengthError,
              timestamp: Date.now(),
              retryable: false,
            }
          });
          return;
        }

        // Executive sends, idle or mid-turn, share the inbox's stable message
        // identity and acknowledgment transport. Only a human resend repeats a POST.
        if (isExecutiveConversationId(get().conversationId)) {
          if (!content.trim()) return;
          const pending = get().sendToInbox(content.trim());
          onAccepted?.();
          set({ inputValue: '', showWelcomeScreen: false });
          return await pending;
        }

        const { settings, addMessage, setResponseStage, currentResponse, conversationId } = get();

        // Block if already processing (but allow if response is idle)
        if (currentResponse && currentResponse.stage !== ResponseStage.IDLE) return;

        const messageContent = content.trim() || (images && images.length > 0 ? 'Images uploaded' : '');
        // The typed message, for every attempt it takes from here: a failed
        // POST, the Retry, the replay after a Reconnect reload.
        const messageSendId = sendId || mintSendId();
        // Whether this turn reached the server's end-of-turn marker, for what was sent to it mid-turn.
        const turnStartedAt = Date.now();
        let turnStarted = false;
        const prepareAndSend = async (requestId: string, attempt?: DeliveryAttempt) => {
          turnStarted = true;
          addChatBreadcrumb('send_message', {
            hasImages: Boolean(images?.length), hidden: Boolean(hidden), messageLength: content.length,
          });
          _conversationMessageCount++;
          try {
            posthog.capture(EVENTS.CONVERSATION.MESSAGE_SENT, {
              conversation_id: conversationId ?? null,
              message_length: content.trim().length,
              has_images: Boolean(images?.length),
              ...analyticsSurface(conversationId),
            });
          } catch { /* Analytics must not prevent an accepted Send. */ }

          // Clear previous response state when starting new message
          set({ currentResponse: null, responseStage: ResponseStage.IDLE });

          // Set response stage immediately to WAITING state
          setResponseStage(ResponseStage.WAITING, 'Leo is thinking...');
          // A resend of unread mid-turn messages is under way as this turn now.
          if (get().inboxResending) set({ inboxResending: false });

          // Add user message immediately with pre-uploaded images
          addMessage({
            type: MessageType.USER,
            content: messageContent,
            status: MessageStatus.SENT,
            images: images, // Use pre-uploaded images directly
            hidden: hidden, // Pass the hidden flag to the message
            display, // Optional display text shown in UI instead of content
          });

          // Composer state belongs to the user until this store accepts the
          // message. Empty/oversized/busy/preparation refusals must not erase it.
          onAccepted?.();

          // Clear input, error, and any stream interrupted state immediately
          // If user sends a new message while retry banner is showing, they're choosing to proceed
          clearInterruptionTimers();
          set({
            inputValue: '',
            error: null,
            showWelcomeScreen: false,
            streamInterrupted: initialStreamInterruptedState,
          });

          if (settings.mockMode) {
            await get()._handleMockResponse(messageContent, requestId);
          } else {
            await get()._handleRealResponse(messageContent, requestId, conversationId, images, false, hidden, display, attempt);
          }
        };
        try {
          if (settings.mockMode) await prepareAndSend(generateId());
          else await withDeliveryAttempt(conversationId, attempt => prepareAndSend(attempt.requestId, attempt), messageSendId);
        } catch (error) {
          // Log technical details for debugging
          console.error('Send message error:', error);
          
          // Check for specific error codes
          const errorCode = (error as any)?.code;
          const isSubscriptionError = errorCode === 'SUBSCRIPTION_REQUIRED';
          
          // If subscription error, dispatch event to show subscription modal
          if (isSubscriptionError) {
            console.log('🔔 [chatbotStore] Subscription required in sendMessage - dispatching event');
            window.dispatchEvent(new CustomEvent('subscription-required'));
            
            // Don't show error in chat - the modal will handle it
            get().setResponseStage(ResponseStage.IDLE, '');
            setTimeout(() => {
              set({ currentResponse: null, responseStage: ResponseStage.IDLE });
            }, 100);
            return;
          }

          // An unauthenticated refusal is not a Leo failure and Retry cannot
          // cure it — see handleSessionExpired.
          if (isSessionExpiredError(error)) {
            get().handleSessionExpired(messageContent, images, hidden, display, {
              sendId: messageSendId,
              acknowledged: deliveryErrorContext(error)?.acknowledged ?? false,
              postMayHaveLanded: deliveryErrorContext(error)?.postMayHaveLanded ?? false,
              newConversation: !conversationId,
            });
            return;
          }

          // For non-subscription errors, show calm stream interrupted UI
          const userFriendlyMessage = 'Having trouble connecting. Please try again in a moment.';

          get().handleStreamInterrupted(
            '',
            userFriendlyMessage,
            true,
            messageContent,
            images,
            hidden,
            display,
            undefined,
            { sendId: messageSendId },
          );
        } finally {
          // Only a completed turn settles here; after a dropped stream the turn may still be
          // running, and what it has not read stays queued for it or for the next turn.
          const sameConversation = !conversationId || get().conversationId === conversationId;
          if (turnStarted && sameConversation && (get().answerCompletedAt ?? 0) >= turnStartedAt) {
            void get().settleInbox(true);
          }
        }
      },

      retryMessage: async (messageId: string) => {
        const { messages } = get();
        const message = messages.find(m => m.id === messageId);

        if (message && message.type === MessageType.USER) {
          // Update message status
          get().updateMessage(messageId, {
            status: MessageStatus.RETRYING,
            retryCount: (message.retryCount || 0) + 1
          });

          // Retry sending (only if content is a string)
          if (typeof message.content === 'string') {
            await get().sendMessage(
              message.content,
              message.images,
              message.hidden,
              message.display,
            );
          }
        }
      },

      cancelCurrentResponse: () => {
        const { conversationId } = get();
        // Signal worker to stop cooperatively (no-op when streaming direct)
        if (conversationId && isExecutiveConversationId(conversationId)) {
          // Stop is a workflow cancel; one that does not land is said, not swallowed.
          set({ executiveNotice: null });
          void NewConversationService.cancelStream(conversationId).then(accepted => {
            if (!accepted && get().conversationId === conversationId) {
              set({ executiveNotice: { kind: 'stop_failed', text: 'Stop did not reach the executive; the turn may still be running.' } });
            }
          });
        } else if (conversationId) {
          NewConversationService.cancelStream(conversationId).catch(() => {});
        }
        // Abort the network request so we stop reading the stream.
        // Unlike detachFromStream, we keep messages visible so the user
        // can see the partial response they chose to stop.
        chatbotApi.cancelRequest(conversationId ?? undefined);

        // Best-effort: finalize any in-progress tool markers on the last assistant message
        // so tool spinners don't get stuck after a client-side abort.
        try {
          const { messages } = get();
          const lastAssistantIndex = [...messages].reverse().findIndex(m => m.type === MessageType.ASSISTANT);
          if (lastAssistantIndex !== -1) {
            const idx = messages.length - 1 - lastAssistantIndex;
            const lastAssistant = messages[idx];
            if (lastAssistant?.toolMarkers?.length) {
              const finalized = lastAssistant.toolMarkers.map(marker => ({
                ...marker,
                success: marker.success === null || marker.success === undefined ? false : marker.success,
              }));
              const updated = [...messages];
              updated[idx] = { ...lastAssistant, toolMarkers: finalized };
              set({ messages: updated });
            }
          }
        } catch (e) {
          console.warn('⚠️ [chatbotStore] Failed to finalize tool markers on cancel', e);
        }

        // Reset state to idle and clear any interruption/error UI so Stop stays quiet.
        set({
          currentResponse: null,
          responseStage: ResponseStage.IDLE,
          streamInterrupted: initialStreamInterruptedState,
          error: null,
        });
        // Stopped: what Leo has not read goes back to the composer, unsent.
        void get().settleInbox(false);
      },

      // Retry from error without adding duplicate user message
      retryFromError: async () => {
        const { error, conversationId, setResponseStage } = get();
        if (isExecutiveConversationId(conversationId) && error?.code === 'CONVERSATION_LOAD_ERROR' && conversationId) {
          await get().loadConversationById(conversationId, { background: true });
          return;
        }

        if (!error?.originalRequest || typeof error.originalRequest !== 'string') {
          console.warn('❌ [chatbotStore] No original request to retry');
          return;
        }

        const messageContent = error.originalRequest;

        try {
          await withDeliveryAttempt(conversationId, async attempt => {
            // Include Retry preparation, not just the eventual transport call.
            set({ error: null });
            setResponseStage(ResponseStage.WAITING, 'Leo is thinking...');
            // Call _handleRealResponse directly without adding a new user message
            // Pass isRetry=true to tell backend not to save duplicate user message
            await get()._handleRealResponse(messageContent, attempt.requestId, conversationId, undefined, true, undefined, undefined, attempt);
          });
        } catch (retryError) {
          console.error('❌ [chatbotStore] Retry failed:', retryError);
          set({ error, currentResponse: null, responseStage: ResponseStage.IDLE });
        }
      },

      clearConversation: () => {
        // Capture conversation ended before clearing
        _captureConversationEnded(get().conversationId);
        // Detach from any in-flight stream, then fully reset the
        // conversation identity so the UI shows the welcome screen.
        get().detachFromStream();
        clearChatContext();
        addChatBreadcrumb('clear_conversation');
        set({
          conversationId: undefined,
          inbox: [],
          showWelcomeScreen: true,
          conversationLoading: false,
          inputValue: '',
          currentView: ChatbotView.CHAT,
          activeDetailRef: null,
          messageCapacity: null,
          executionTodo: null,
        });
      },

      // ── Stream lifecycle ──────────────────────────────────────────
      //
      //   detachFromStream       – close HTTP, clear messages/transient state,
      //                            keep conversationId (workspace switch)
      //   clearConversation      – detach + forget conversation (new chat)
      //   cancelCurrentResponse  – signal worker + close HTTP, keep messages
      //                            visible (stop button)
      //
      detachFromStream: () => {
        // Disconnect the frontend from an in-flight SSE stream WITHOUT
        // cancelling the backend worker. The worker will finish execution
        // and save the complete response to S3, so the conversation is
        // available in history when the user returns to this workspace.
        const { conversationId } = get();
        chatbotApi.cancelRequest(conversationId ?? undefined);
        clearInterruptionTimers();
        set({
          messages: isExecutiveConversationId(conversationId) ? get().messages : [],
          inbox: isExecutiveConversationId(conversationId) ? get().inbox : [],
          currentResponse: null,
          responseStage: ResponseStage.IDLE,
          error: null,
          executionTodo: null,
          streamInterrupted: initialStreamInterruptedState,
          sseConnection: initialSSEConnectionState,
          conversationLoading: isExecutiveConversationId(conversationId) ? get().messages.length === 0 : true,
          showWelcomeScreen: false,
        });
      },

      // Input handling
      setInputValue: (value: string) => {
        set({ inputValue: value });
      },
      setInputFocused: (value: boolean) => {
        set({ inputFocused: value });
      },
      setInputPlaceholderOverride: (value: string | null) => {
        set({ inputPlaceholderOverride: value });
      },
      requestInputFocus: () => {
        // Bump a monotonically increasing value so components can react and focus.
        set((state) => ({ inputFocusRequestId: state.inputFocusRequestId + 1 }));
      },
      openArtifactPicker: ({ section = null, restrictTo = null }) => {
        // A one-shot request that an external entry point (e.g. the Overview
        // "Tune" button) posts on the global panel store. The composer watches
        // it, adopts it into its own local picker state, opens pre-drilled,
        // then clears it via closeArtifactPicker. It is NOT live drill state —
        // that lives in ChatbotInput, which owns the picker.
        set({ artifactPickerSection: section, artifactPickerRestrictTo: restrictTo });
      },
      closeArtifactPicker: () => set({ artifactPickerSection: null, artifactPickerRestrictTo: null }),
      clearPendingArtifacts: () => {
        if (get().pendingArtifacts.length > 0) {
          set({ pendingArtifacts: [] });
        }
      },

      // Progress tracking
      updateResponseProgress: (progress: Partial<ResponseProgress>) => {
        set(state => {
          const updates: any = {
            currentResponse: state.currentResponse
              ? { ...state.currentResponse, ...progress }
              : null
          };

          // If progress includes messageCapacity, also update root-level messageCapacity
          if (progress.messageCapacity) {
            updates.messageCapacity = progress.messageCapacity;
          }
          if (progress.executionTodo !== undefined) {
            updates.executionTodo = progress.executionTodo;
          }

          return updates;
        });
      },

      // The answer is done arriving; the post-turn phase may still be running.
      // Purely a notification signal — nothing about the UI reads it, so the
      // spinner and cancel affordance keep their existing lifetime.
      markAnswerComplete: (pending?: TurnPending | null) => {
        set({ answerCompletedAt: Date.now(), answerPending: pending ?? null });
      },

      sendToInbox: async (text: string, resendKey?: string) => {
        const conversationId = get().conversationId;
        const message = text.trim();
        if (!conversationId || !message) return null;
        if (isExecutiveConversationId(conversationId)) {
          const sessionCurrent = captureExecutiveSession(conversationId);
          const prepared = prepareExecutiveSend(get().inbox, message, mintSendId, resendKey);
          const key = prepared.key;
          const optimistic = get().responseStage === ResponseStage.IDLE || get().responseStage === ResponseStage.ERROR;
          set({ inbox: prepared.inbox, showWelcomeScreen: false, conversationLoading: false, error: null });
          if (optimistic) get().setResponseStage(ResponseStage.WAITING, 'Leo is thinking...');
          // The workflow owns an executive steer: it lands in the running turn or waits, queued.
          try {
            const ack = await NewConversationService.steerExecutive(conversationId, message, key);
            if (get().conversationId !== conversationId || !sessionCurrent()) return null;
            set({
              inbox: dropSavedExecutiveInbox(acknowledgeExecutiveSend(get().inbox, key, ack.msgId, ack.mode), get().messages),
              executiveSteer: ack,
              executiveNotice: null,
            });
            if (optimistic && ack.mode === 'duplicate') set({ responseStage: ResponseStage.IDLE, currentResponse: null });
            void get().syncStreamLifecycle();
            return null;
          } catch (error) {
            if (get().conversationId !== conversationId || !sessionCurrent()) return null;
            if (optimistic && get().responseStage === ResponseStage.WAITING) set({ responseStage: ResponseStage.IDLE, currentResponse: null });
            const refusal = executiveSendRefusal(error) ?? {
              kind: 'refused' as const,
              text: `This message was not sent: ${error instanceof Error ? error.message : 'The request was refused.'}`,
            };
            // Preserve refused text in its bubble. The composer may already hold
            // a newer draft; uncertain delivery keeps the original resend identity.
            set({
              inbox: refusal.kind === 'unavailable'
                ? retainUncertainExecutiveSend(get().inbox, key)
                : get().inbox.map(item => item.key === key ? { ...item, status: 'failed' as const } : item),
              executiveNotice: refusal,
            });
            return refusal.text;
          }
        }
        const key = generateId();
        set({ inbox: queueInboxItem(get().inbox, key, message) });
        const { id, error } = await NewConversationService.postInbox(conversationId, message);
        if (get().conversationId !== conversationId) return null;
        if (!id) {
          set({ inbox: dropInboxItem(get().inbox, key) });
          return error ?? null;
        }
        set({ inbox: acceptInboxItem(get().inbox, key, id) });
        // Accepted after the turn ended: no running turn will read it, so it goes on now.
        if (get().responseStage === ResponseStage.IDLE && !chatbotApi.hasActiveStream(conversationId)) {
          void get().settleInbox(true);
        }
        return null;
      },

      markInboxDelivered: (ids: string[]) => {
        if (ids.length) set({ inbox: markInboxRead(get().inbox, ids) });
      },

      refreshInbox: async () => {
        const conversationId = get().conversationId;
        // An executive steer waits in the workflow, not the Redis inbox (the tab polls it).
        if (!conversationId || isExecutiveConversationId(conversationId)) return;
        const pending = await NewConversationService.getInbox(conversationId);
        if (pending === null || get().conversationId !== conversationId) return;
        set({ inbox: dropSavedInboxItems(mergeServerPending(get().inbox, pending), get().messages) });
      },

      settleInbox: async (completed: boolean) => {
        const conversationId = get().conversationId;
        if (!conversationId) return;
        if (isExecutiveConversationId(conversationId)) {
          // Never taken back and re-sent: the workflow already holds what the turn did not read.
          const inbox = dropSavedExecutiveInbox(get().inbox, get().messages);
          set({ messages: foldExecutiveSteers(get().messages, inbox.filter(item => item.status === 'read'), Date.now()),
            inbox: queueExecutiveLeftovers(inbox.filter(item => item.status !== 'read')) });
          return;
        }
        const folded = foldReadInboxItems(get().messages, get().inbox, inboxItemToMessage);
        const waiting = folded.inbox.filter(item => item.status === 'pending' && item.id);
        const waitingKeys = new Set(waiting.map(item => item.key));
        if (waiting.length === 0) {
          if (folded.messages !== get().messages) set({ messages: folded.messages, inbox: folded.inbox });
          return;
        }
        // Taken out of `pending` while the DELETEs run, so an overlapping settle leaves them alone.
        set({
          messages: folded.messages,
          inbox: folded.inbox.map(item => (waitingKeys.has(item.key) ? { ...item, status: 'sending' as const } : item)),
        });
        const consumed = new Map(await Promise.all(waiting.map(async item =>
          [item.key, await NewConversationService.consumeInboxItem(conversationId, item.id!)] as const)));
        if (get().conversationId !== conversationId) return;
        const plan = planTurnEnd(waiting, consumed, completed);
        // What this client took back keeps its bubble until it goes on as the next message.
        const taken = new Set(waiting.filter(item => consumed.get(item.key) === true).map(item => item.key));
        const refolded = foldReadInboxItems(
          get().messages,
          [...get().inbox.filter(item => !waitingKeys.has(item.key) || taken.has(item.key)), ...plan.inbox],
          inboxItemToMessage,
        );
        set({ messages: refolded.messages, inbox: refolded.inbox });
        let restore = plan.restore;
        if (plan.send) {
          await new Promise(resolve => setTimeout(resolve, TURN_SETTLE_MS));
          if (get().conversationId !== conversationId) return;
          const busy = get().responseStage !== ResponseStage.IDLE || chatbotApi.hasActiveStream(conversationId);
          if (busy) restore = plan.send;
        }
        const resend = Boolean(plan.send && !restore);
        // The ask goes on as the next turn: the completion alert waits for it (chatTurnChime.ts).
        set({ inbox: get().inbox.filter(item => !taken.has(item.key)), ...(resend ? { inboxResending: true } : {}) });
        if (resend) {
          void get().sendMessage(plan.send).finally(() => {
            // A send that never started a turn ends the ask here.
            if (get().inboxResending) set({ inboxResending: false });
          });
        }
        if (restore) {
          const draft = get().inputValue;
          set({ inputValue: draft.trim() ? `${restore}\n\n${draft}` : restore });
        }
      },

      setResponseStage: (stage: ResponseStage, activity?: string) => {
        const now = Date.now();

        set(state => ({
          responseStage: stage,
          currentResponse: {
            requestId: state.currentResponse?.requestId || generateId(),
            stage,
            progress: getProgressForStage(stage),
            startTime: state.currentResponse?.startTime || now,
            estimatedDuration: 15000, // 15 seconds default
            currentActivity: activity || getDefaultActivityForStage(stage),
            canCancel: stage !== ResponseStage.IDLE && stage !== ResponseStage.ERROR,
          }
        }));
      },

      setActiveTools: (tools: string[]) => {
        set({ activeTools: tools });
      },

      setExecutionTodo: (todo: ExecutionTodo | null) => {
        set({ executionTodo: todo });
      },

      // Error handling
      setError: (error: ChatbotError | null) => {
        get().setResponseStage(ResponseStage.ERROR, 'Error occurred');
        set({ error: error });
      },

      clearError: () => {
        set({ error: null });
      },

      // Settings
      updateSettings: (newSettings: Partial<ChatbotSettings>) => {
        set(state => ({
          settings: { ...state.settings, ...newSettings }
        }));
      },

      toggleMockMode: () => {
        set(state => ({
          settings: {
            ...state.settings,
            mockMode: !state.settings.mockMode
          }
        }));
      },

      // Conversation management
      setConversationId: (id: string | undefined) => {
        const currentId = get().conversationId;

        // If switching to a different conversation (or clearing), reset capacity
        // Capacity will be recalculated when loadConversationById is called
        if (currentId !== id) {
          set({
            conversationId: id,
            messageCapacity: null, // Reset capacity when switching conversations
            executionTodo: null,
          });
        } else {
          set({ conversationId: id });
        }

      },

      loadConversationFromStorage: () => {
        // This method is now redundant since Zustand persist handles hydration
        // Keeping for backwards compatibility but it's a no-op
        const { conversationId } = get();
        if (conversationId) {
          // Already loaded via persist middleware
          return;
        }
      },

      // Reconcile this store's FULL visible state with reality. The chat's
      // state has exactly two sources of truth — S3 owns completed turns, the
      // per-turn Redis event log owns the in-flight turn — and this action is
      // deliberately STATELESS about which entry point invoked it: if the
      // store is unhydrated it loads S3 first, then attaches the read-only
      // tail (full replay from seq 0) for any live turn. Any mount / switch /
      // reload path converges to the same derived state.
      //  - this client already holds the live reader → nothing to do;
      //  - store empty → hydrate via initializeStore (which re-enters here
      //    with skipHydration after loading);
      //  - a worker turn is in flight → attachToLiveStream;
      //  - nothing in flight but the UI thinks it is streaming → settle idle.
      syncStreamLifecycle: async (opts?: { skipHydration?: boolean; isCurrentScope?: () => boolean; isExecutiveInFlight?: () => boolean }) => {
        if (opts?.isCurrentScope && !opts.isCurrentScope()) return;
        const { conversationId, showWelcomeScreen, messages } = get();
        if (!conversationId || get().error?.code === 'CONVERSATION_ACCESS_DENIED') return;
        // A draft has a client-minted id the backend has never seen — no status
        // to check, and nothing to attach to.
        if (showWelcomeScreen && messages.length === 0) return;
        // Single-flight: a live reader (send or attach) already owns this
        // conversation's stream in this tab.
        if (chatbotApi.hasActiveStream(conversationId)) return;

        // Self-sufficiency: never assume some other mount path hydrated the
        // store. skipHydration marks the re-entry from initializeStore itself
        // (messages may legitimately still be empty — a brand-new conversation
        // mid-first-turn) so this cannot recurse.
        if (!opts?.skipHydration && messages.length === 0) {
          await get().initializeStore();
          return;
        }

        try {
          const { isActive, turnId } = await NewConversationService.getStreamStatus(conversationId);
          if (get().conversationId !== conversationId || (opts?.isCurrentScope && !opts.isCurrentScope())) return;
          // Re-check after the await — a send may have started while we polled.
          if (chatbotApi.hasActiveStream(conversationId)) return;
          if (isActive) {
            console.log(`🔄 [chatbotStore] Active stream detected for ${conversationId}. Attaching read-only tail...`);
            await get().attachToLiveStream(turnId, opts?.isCurrentScope, opts?.isExecutiveInFlight);
          } else if (isExecutiveConversationId(conversationId) && (opts?.isExecutiveInFlight?.() || hasPendingExecutiveInbox(get().inbox))) {
            // Temporal admission can precede the kernel stream. Its accepted
            // inbox message still owns WAITING until read, refused, or settled.
            if (get().responseStage !== ResponseStage.WAITING) {
              get().setResponseStage(ResponseStage.WAITING, 'Leo is thinking...');
            }
          } else if (get().responseStage !== ResponseStage.IDLE) {
            // Stale busy state from a previous session/tab — nothing is running.
            set({ responseStage: ResponseStage.IDLE, currentResponse: null });
          }
        } catch {
          // best-effort
        }
      },

      // Attach to an in-flight worker turn WITHOUT sending anything: read-only
      // tail via GET /subscribe, full replay from seq 0. Unlike resumeStream
      // (the interrupted-banner path, which re-POSTs the original message),
      // this can never enqueue a turn — so it is safe to fire from any mount /
      // switch / reload, even if the turn completes while we're attaching.
      attachToLiveStream: async (streamTurnId?: string, isCurrentScope?: () => boolean, isExecutiveInFlight?: () => boolean) => {
        const { conversationId } = get();
        if (!conversationId || get().error?.code === 'CONVERSATION_ACCESS_DENIED') return;
        if (chatbotApi.hasActiveStream(conversationId)) return;
        const executive = isExecutiveConversationId(conversationId);
        // The backend saves the opening before publishing the live turn. Bring
        // its harness card into the shared timeline before the new reply starts.
        if (executive) {
          await get().loadConversationById(conversationId, { background: true, isCurrentScope, keepLiveTail: true });
          if (get().conversationId !== conversationId || get().error?.code === 'CONVERSATION_ACCESS_DENIED' ||
            (isCurrentScope && !isCurrentScope()) || chatbotApi.hasActiveStream(conversationId)) return;
        }
        let turnId = executive || streamTurnId?.startsWith('continuation_') ? streamTurnId : undefined;
        // What the person sent mid-turn before this page (re)attached.
        const attachedAt = Date.now();
        void get().refreshInbox();

        const upsertLiveMessage = (content: string, toolMarkers?: ToolMarker[], rewind = false) => {
          if (get().conversationId !== conversationId || get().error?.code === 'CONVERSATION_ACCESS_DENIED' || (isCurrentScope && !isCurrentScope())) return;
          const result = upsertTurnReply(get().messages, content, toolMarkers, turnId, rewind);
          if (result.action === 'update') {
            set({ messages: result.messages });
          } else if (result.action === 'append') {
            get().addMessage({
              type: MessageType.ASSISTANT,
              content,
              status: MessageStatus.SENT,
              toolMarkers,
              turnId,
            });
          }
        };

        // Clear any hydration skeleton immediately — the attach owns the surface
        // now — but do NOT flip to the "working" state up front. A phantom
        // "in_progress" status (stale metadata, no live stream — /subscribe then
        // 404s) would otherwise flash the working spinner on every reconnect for a
        // corpse. Enter the working state lazily, only once the tail delivers a
        // genuinely live event; a 404 / already-finished stream delivers none, so
        // there is nothing to flash.
        set({ conversationLoading: false });

        let enteredLiveState = false;
        const enterLiveState = () => {
          if (enteredLiveState) return;
          enteredLiveState = true;
          // BotMessage hands currentResponse to the last assistant. Create this
          // activation's bubble first, even when only thinking has arrived, so
          // that handoff cannot hide the previous completed conclusion.
          if (executive && turnId) upsertLiveMessage('');
          set({
            responseStage: ResponseStage.STREAMING,
            currentResponse: {
              requestId: `attach_${Date.now()}`,
              turnId,
              stage: ResponseStage.STREAMING,
              progress: 50,
              startTime: Date.now(),
              estimatedDuration: 10000,
              currentActivity: 'Reconnecting...',
              canCancel: true,
            },
          });
        };

        let lastContent = '';
        let lastMarkerSignature = '';
        // Latches true on the first node-tagged progress. Once the stream is
        // known node-aware, only conclusion_render text is the answer — later
        // payloads that lack node info (capacity_info, terminal summaries)
        // carry the RAW multi-node fullText and must never reach the bubble.
        let nodeTaggedSeen = false;
        try {
          const response = await chatbotApi.attachStream(conversationId, (progress: ResponseProgress) => {
            // Guard against a chat-switch racing the tail (same contract as
            // _handleRealResponse's isStaleStream).
            if (get().conversationId !== conversationId || (isCurrentScope && !isCurrentScope())) return;
            // The actual tail wins if a turn finished between status and subscribe.
            if (progress.turnId) {
              turnId = executive || progress.turnId.startsWith('continuation_') ? progress.turnId : undefined;
            }
            // Enter the working state only on a GENUINE live event — one carrying
            // streamed text or tool markers. attachStream emits a synthetic
            // "Starting stream..." (WAITING, no payload) BEFORE it opens the
            // subscribe request; a phantom "in_progress" status then 404s. Gating
            // on real payload keeps that corpse from flashing the spinner while
            // still lighting up the instant an actual live turn replays. Skip the
            // check once we've entered — the shape-sniff is pure overhead then.
            if (!enteredLiveState) {
              const hasLivePayload =
                progress.stage !== ResponseStage.IDLE &&
                ((typeof progress.streamedContent === 'string' && progress.streamedContent.length > 0) ||
                  (typeof progress.conclusionContent === 'string' && progress.conclusionContent.length > 0) ||
                  (Array.isArray(progress.toolMarkers) && progress.toolMarkers.length > 0));
              if (hasLivePayload) enterLiveState();
            }
            if (progress.nodeTagged) nodeTaggedSeen = true;
            if (progress.isReplyReset) {
              // The answer so far is taken back; the steps stay.
              lastContent = '';
              upsertLiveMessage('', progress.toolMarkers, true);
              get().updateResponseProgress(progress);
              return;
            }
            const streamed = nodeTaggedSeen
              ? (typeof progress.conclusionContent === 'string' ? progress.conclusionContent : null)
              : progress.streamedContent;
            const markerSignature = (progress.toolMarkers || [])
              .map(m => `${m.runId ?? m.position}:${m.tool}:${m.message ?? ''}:${m.success ?? 'p'}`)
              .join('|');
            const textChanged = typeof streamed === 'string' && streamed.length > 0 && streamed !== lastContent;
            const markersChanged = markerSignature !== lastMarkerSignature;
            // Materialize/refresh the bubble on the first tool marker OR the
            // first answer text — same contract as the send pipeline, so a
            // reattached tool phase shows the WorkingSection instead of a bare
            // spinner with no bubble.
            if (textChanged || (markersChanged && markerSignature)) {
              if (textChanged) lastContent = streamed as string;
              lastMarkerSignature = markerSignature;
              const { content: cleanedContent } = parseSuggestedResponses(lastContent);
              upsertLiveMessage(cleanedContent, progress.toolMarkers);
            }
            get().updateResponseProgress(progress);
          }, streamTurnId);
          // Completion fallback, mirroring the send pipeline's reconciliation:
          // if nothing ever streamed into the answer body — a non-node stream
          // that ended between events, or a code-mode turn that never opened a
          // <conclusion> wrapper (so no conclusion_render tokens exist) —
          // apply the finished turn's full text. Only when the body is still
          // empty: a streamed conclusion is never overwritten with raw text.
          if (
            !lastContent &&
            response?.content && typeof response.content === 'string' && response.content.length > 0
          ) {
            const { content: cleanedContent } = parseSuggestedResponses(response.content);
            upsertLiveMessage(cleanedContent);
          }
        } catch (error) {
          // Read-only tail failed (network, tail closed, flag off). The turn
          // keeps running server-side and lands in S3; nothing to roll back.
          console.warn('⚠️ [chatbotStore] attachToLiveStream tail ended with error:', error);
        } finally {
          // Settle to idle only when nothing else took over the stream — a real
          // send supersedes the attach (aborting it), and ITS lifecycle now owns
          // responseStage.
          if (get().conversationId === conversationId && (!isCurrentScope || isCurrentScope()) && !chatbotApi.hasActiveStream(conversationId)) {
            if (get().error?.code !== 'CONVERSATION_ACCESS_DENIED' && isExecutiveConversationId(conversationId) &&
              (hasPendingExecutiveInbox(get().inbox) || isExecutiveInFlight?.())) {
              // A finished/failed reader does not settle newer Temporal ingress.
              // Reuse the shared waiting state; the next authoritative poll owns convergence.
              get().setResponseStage(ResponseStage.WAITING, 'Leo is thinking...');
            } else {
              set({ responseStage: ResponseStage.IDLE, currentResponse: null });
            }
            if ((get().answerCompletedAt ?? 0) >= attachedAt) void get().settleInbox(true);
            // The tail carries the answer text only; its chips, reasoning and turn
            // boundaries are in the save. Converge on it, so a chat watched from a
            // second tab or reopened mid-turn ends exactly as a reload shows it.
            if (enteredLiveState) {
              void get().loadConversationById(conversationId, { background: true, isCurrentScope, keepLiveTail: true });
            }
          }
        }
      },

      // This MUST be called by the chatbot component on mount to load conversation history
      // Single-flight per conversation: mount, chat-switch, the rehydrate
      // listener, and syncStreamLifecycle's self-hydration all converge on ONE
      // run instead of racing concurrent loads over a live attach.
      initializeStore: () => singleFlight(initializeInFlight, get().conversationId, async () => {
        const { conversationId, messages, showWelcomeScreen } = get();

        // A DRAFT (welcome screen, no messages) has a client-minted id the
        // backend has never seen — loading it would 404 and wipe the id out
        // from under the panel. Leave it alone until the first send.
        if (conversationId && showWelcomeScreen && messages.length === 0) {
          set({ conversationLoading: false });
          return;
        }

        // If we have a saved conversation ID, attempt to load it from S3 if messages are empty
        // or if showWelcomeScreen was left on from a draft chat session.
        if (conversationId && (messages.length === 0 || showWelcomeScreen)) {
          console.log('🚀 [chatbotStore] Initializing - loading conversation from S3:', conversationId);
          try {
            await get().loadConversationById(conversationId);
            console.log('✅ [chatbotStore] Conversation loaded successfully from S3');
            await get().syncStreamLifecycle({ skipHydration: true });
          } catch (error) {
            console.error('❌ [chatbotStore] Failed to load conversation:', error);
            // Check if stream is active in Redis before discarding the conversation ID
            try {
              const status = await NewConversationService.getStreamStatus(conversationId);
              if (status.isActive) {
                console.log('🔄 [chatbotStore] Stream is active despite load failure, auto-resuming stream');
                set({ showWelcomeScreen: false, conversationLoading: false });
                await get().syncStreamLifecycle({ skipHydration: true });
                return;
              }
            } catch (statusError) {
              console.warn('⚠️ [chatbotStore] Failed checking stream status during fallback:', statusError);
            }
            // Clear the invalid conversation ID and show welcome screen
            set({
              conversationId: undefined,
              showWelcomeScreen: true,
              conversationLoading: false,
            });
          }
        } else if (conversationId && messages.length > 0) {
          // Messages already in memory — still check for an active in-flight worker stream
          await get().syncStreamLifecycle({ skipHydration: true });
        } else if (!conversationId) {
          // No saved conversation - make sure loading state is cleared
          set({ conversationLoading: false });
        }
      }),

      // Chat history management
      setCurrentView: (view: ChatbotView) => {
        set({ currentView: view });
      },

      // Full-state detail takeover — the card's info button opens a platform-read
      // detail view in place of the message list; null returns to the chat.
      setActiveDetailRef: (ref) => {
        set({ activeDetailRef: ref });
      },

      loadConversationHistory: async () => {
        set({ historyLoading: true });
        try {
          const conversations = await NewConversationService.getAll();
          const historyItems: ConversationHistoryItem[] = conversations.map(conv => ({
            conversation_id: conv.conversation_id,
            title: conv.title,
            timestamp: conv.timestamp,
            last_activity_at: conv.last_activity_at,
          }));

          set({
            conversationHistory: historyItems,
            historyLoading: false
          });
        } catch (error) {
          console.error('Failed to load conversation history:', error);
          set({
            historyLoading: false,
            error: {
              id: generateId(),
              code: 'HISTORY_LOAD_ERROR',
              message: 'Failed to load conversation history',
              timestamp: Date.now(),
              retryable: true,
            }
          });
        }
      },

      saveCurrentConversation: async () => {
      const { messages, conversationId } = get();

      if (!messages.length || !conversationId) return;

      try {
        const nowEpochSeconds = Math.floor(Date.now() / 1000);
        const historyItem: ConversationHistoryItem = {
          conversation_id: conversationId,
          title: deriveConversationTitle(messages),
          timestamp: nowEpochSeconds,
          last_activity_at: nowEpochSeconds,
        };

        set(state => ({
          conversationHistory: [historyItem, ...state.conversationHistory.filter(
            item => item.conversation_id !== conversationId
          )]
        }));
      } catch (error) {
        console.error('Failed to save conversation:', error);
      }
    },

    deleteConversation: async (conversationId: string) => {
      try {
        // Delete from backend
        await NewConversationService.delete(conversationId);

        // Remove from local history
        set(state => ({
          conversationHistory: state.conversationHistory.filter(
            item => item.conversation_id !== conversationId
          )
        }));

        // If the deleted conversation is the current one, clear it
        const { conversationId: currentId } = get();
        if (currentId === conversationId) {
          get().clearConversation();
        }
      } catch (error) {
        console.error('Failed to delete conversation:', error);
        get().setError({
          id: generateId(),
          code: 'DELETE_ERROR',
          message: 'Failed to delete conversation. Please try again.',
          timestamp: Date.now(),
          retryable: true,
        });
        throw error;
      }
    },

    // Internal actions
    addMessage: (messageData) => {
      const baseMessage = createMessage(
        messageData.type,
        messageData.content,
        messageData.status,
        messageData.images,
        messageData.hidden,
        messageData.display
      );
      // Merge any additional fields that createMessage doesn't accept positionally
      // (thinkingContent, toolsUsed, toolMarkers, suggestedResponses, etc.) so
      // callers can pass them at construction time.
      const message: ChatbotMessage = { ...messageData, ...baseMessage };

      set(state => {
        const newMessages = [...state.messages, message];
        // Don't recalculate capacity here - use backend-provided capacity
        // Capacity is updated via capacity_info event from backend

        return {
          messages: newMessages,
          showWelcomeScreen: false, // Hide welcome screen when message is added
          // Keep existing messageCapacity from backend
        };
      });
    },

    updateMessage: (id: string, updates: Partial<ChatbotMessage>) => {
      set(state => {
        const updatedMessages = state.messages.map(msg =>
          msg.id === id ? { ...msg, ...updates } : msg
        );
        
        // Don't recalculate capacity here - use backend-provided capacity
        // Capacity is updated via capacity_info event from backend
        
        return {
          messages: updatedMessages,
          // Keep existing messageCapacity from backend
        };
      });
    },

    setState: (newState: ChatbotState) => {
      // State is automatically persisted via Zustand's persist middleware (workspace-aware)
      set({ state: newState });
    },

    // Internal helper methods
    _handleMockResponse: async (content: string, _requestId: string) => {
      const { setResponseStage, addMessage } = get();

      // Simple delay
      await new Promise(resolve => setTimeout(resolve, 3000));

      // Add mock response (can be string or structured content)
      const mockResponse = getMockResponse(content);
      addMessage({
        type: MessageType.ASSISTANT,
        content: mockResponse,
        status: MessageStatus.SENT,
      });

      // Complete - set stage to IDLE
      setResponseStage(ResponseStage.IDLE, 'Complete');

      // Clear currentResponse after a short delay to ensure UI updates
      // This allows the final message to render before clearing the response state
      setTimeout(() => {
        set({ currentResponse: null, responseStage: ResponseStage.IDLE });
      }, 100);
    },

    _handleRealResponse: async (content: string, _requestId: string, conversationId?: string, uploadedImages?: any[], isRetry?: boolean, isHidden?: boolean, displayText?: string, preparedAttempt?: DeliveryAttempt) => {
      const { updateResponseProgress, addMessage, setConversationId, setResponseStage } = get();
      const attempt = preparedAttempt ?? createDeliveryAttempt(conversationId);
      const activeConversationId = attempt.conversationId;
      const isNewConversation = !conversationId;
      let conversationBound = !isNewConversation;
      const isStaleStream = (): boolean => conversationBound && get().conversationId !== activeConversationId;
      try {
        attempt.ensureHeaders();
        attempt.phase = 'conversation_preparation';
        // A new steer clears the last refusal; a new one replaces it if this is refused too.
        if (isExecutiveConversationId(activeConversationId)) set({ executiveNotice: null });
        setChatContext({ conversationId: conversationId ?? 'new' });

        // Generate conversationId for new conversations (format: conv_{timestamp}_{uuid})
        // IMPORTANT: We set conversationId in state immediately but DON'T persist to localStorage yet.
        // This prevents saving an ID that may never be created on the server if the request fails.
        // We persist to localStorage only after successful response (see below).
        if (isNewConversation) {
          // Set in state only (don't persist to localStorage yet)
          set({ conversationId: activeConversationId });
          conversationBound = true;

          // Optimistically insert the new chat into the history cache the moment
          // send begins, so a click on the history icon during streaming already
          // shows it. Rolled back in the catch block if the send fails so a
          // never-created chat doesn't linger. The queryFn merge protects the
          // entry from a background refetch racing DynamoDB indexing.
          const nowEpochSeconds = Math.floor(Date.now() / 1000);
          const historyItem: ConversationHistoryItem = {
            conversation_id: activeConversationId,
            title: deriveConversationTitle(get().messages),
            timestamp: nowEpochSeconds,
            last_activity_at: nowEpochSeconds,
          };
          updateChatHistoryCache(queryClient, { kind: 'insert', item: historyItem });
        }

        let response: Awaited<ReturnType<typeof chatbotApi.sendMessage>> | undefined;

        // Always use streaming (now that we generate conversationId for new conversations)
        let streamedText = '';
        let streamingMessageId: string | null = null;
        let lastMarkerSignature = '';
        const activeToolsSet = new Set<string>();

        const toolsUsedInMessage: string[] = [];
        const toolMarkers: Array<{ position: number; tool: string; runId?: string; message?: string; success?: boolean | null }> = [];

        // Throttle message updates to prevent React error 185 (max update depth)
        // During fast streaming, we only update the messages array every 100ms
        const throttleState = {
          lastUpdateTime: 0,
          pendingUpdate: null as (() => void) | null,
        };
        
        // STRESS TEST: Set to 0 to disable throttling and trigger error 185
        // Add ?stressTest=true to URL to test
        const isStressTest = typeof window !== 'undefined' && 
          new URLSearchParams(window.location.search).get('stressTest') === 'true';
        const MESSAGE_UPDATE_THROTTLE_MS = isStressTest ? 0 : 100;
        
        if (isStressTest) {
          console.warn('⚠️ STRESS TEST MODE: Throttling disabled - expect React error 185!');
        }

        const throttledMessageUpdate = (updateFn: () => void) => {
          const now = Date.now();
          if (now - throttleState.lastUpdateTime >= MESSAGE_UPDATE_THROTTLE_MS) {
            // Enough time has passed, update immediately
            throttleState.lastUpdateTime = now;
            updateFn();
          } else {
            // Store the pending update - it will be applied on next throttle window
            throttleState.pendingUpdate = updateFn;
          }
        };

        // Helper function to perform the streaming request
        const performStreamRequest = async (): Promise<Awaited<ReturnType<typeof chatbotApi.sendMessage>>> => {
          return await chatbotApi.sendMessageStream(
            content,
            activeConversationId!,
            uploadedImages,
            isRetry,
            undefined,
            undefined,
            isHidden,
            (progress) => {
              // GUARD: If the user navigated away (workspace switch, new chat, etc.)
              // ignore all further callbacks so stale data never touches the store.
              if (isStaleStream()) return;

              // Worker death recovery: when the backend sends response_reset, the
              // chatbotApi layer sets isStreamReset=true.  We must clear ALL local
              // streaming state here so the recovery stream starts clean.  Without
              // this, stale toolMarkers, streamedText, and streamingMessageId from
              // the dead worker's partial output persist and mix with the new
              // attempt's content (split-brain state).
              if (progress.isStreamReset) {
                toolMarkers.length = 0;
                streamedText = '';
                streamingMessageId = '';
                lastMarkerSignature = '';
                activeToolsSet.clear();
                toolsUsedInMessage.length = 0;
                get().setActiveTools([]);
                updateResponseProgress(progress);
                return;
              }

              // Sync the streaming assistant message with the latest content and
              // tool markers. Called on both first-tool-marker and first-text-token
              // so tool activity is visible even when Leo runs a long chain of
              // tools before producing any text.
              const syncStreamingMessage = (
                content: string,
                suggestions: ReturnType<typeof parseSuggestedResponses>['suggestions'],
              ) => {
                if (!streamingMessageId) {
                  streamingMessageId = generateId();
                  addMessage({
                    type: MessageType.ASSISTANT,
                    content,
                    status: MessageStatus.SENT,
                    toolMarkers: toolMarkers.length > 0 ? [...toolMarkers] : undefined,
                    suggestedResponses: suggestions.length > 0 ? suggestions : undefined,
                  });
                  const { messages } = get();
                  const lastMessage = messages[messages.length - 1];
                  if (lastMessage) {
                    streamingMessageId = lastMessage.id;
                  }
                  return;
                }
                throttledMessageUpdate(() => {
                  const { messages } = get();
                  const messageIndex = messages.findIndex(m => m.id === streamingMessageId);
                  if (messageIndex !== -1) {
                    const updated = [...messages];
                    updated[messageIndex] = {
                      ...updated[messageIndex],
                      content,
                      toolMarkers: toolMarkers.length > 0 ? [...toolMarkers] : updated[messageIndex].toolMarkers,
                      suggestedResponses: suggestions.length > 0 ? suggestions : updated[messageIndex].suggestedResponses,
                    };
                    set({ messages: updated });

                    // STRESS TEST: Force extra updates to trigger error 185
                    if (isStressTest) {
                      for (let i = 0; i < 5; i++) {
                        set({ messages: [...get().messages] });
                      }
                    }
                  }
                });
              };

              // The harness's activity line (a hand-off, a quiet model) reaches the
              // pill even when nothing else in the payload changed.
              if ('activityNote' in progress && get().currentResponse?.activityNote !== progress.activityNote) {
                updateResponseProgress({ activityNote: progress.activityNote });
              }

              // reply_reset: the answer so far is taken back and will be written
              // anew; the steps and the working text stay.
              if (progress.isReplyReset) {
                streamedText = progress.streamedContent || '';
                updateResponseProgress({ ...progress, toolMarkers: [...toolMarkers], streamedContent: '' });
                if (streamingMessageId) syncStreamingMessage('', []);
                return;
              }

              // Track tool markers at their position in text
              // Update toolMarkers array to match the progress (preserve messages if already set)
              if (progress.toolMarkers && Array.isArray(progress.toolMarkers)) {
                // Merge progress markers with existing markers, preserving messages and success status
                progress.toolMarkers.forEach((progressMarker: { position: number; tool: string; runId?: string; message?: string; success?: boolean | null }) => {
                  // Use runId for matching if available, otherwise fall back to position+tool
                  const existingIndex = progressMarker.runId
                    ? toolMarkers.findIndex(m => m.runId === progressMarker.runId)
                    : toolMarkers.findIndex(m => m.position === progressMarker.position && m.tool === progressMarker.tool);

                  if (existingIndex !== -1) {
                    // Update existing marker, preserve message and success if progress marker has them
                    if (progressMarker.message !== undefined) {
                      toolMarkers[existingIndex] = {
                        ...toolMarkers[existingIndex],
                        runId: progressMarker.runId || toolMarkers[existingIndex].runId, // Preserve or update runId
                        message: progressMarker.message,
                        success: progressMarker.success !== undefined ? progressMarker.success : toolMarkers[existingIndex].success
                      };
                    } else if (progressMarker.success !== undefined) {
                      toolMarkers[existingIndex] = {
                        ...toolMarkers[existingIndex],
                        success: progressMarker.success
                      };
                    }
                  } else {
                    // Add new marker
                    toolMarkers.push(progressMarker);
                  }
                });
              }

              // Track tools - update activeToolsSet from progress
              if (progress.activeTools && Array.isArray(progress.activeTools)) {
                progress.activeTools.forEach((tool: string) => {
                  activeToolsSet.add(tool);
                  if (!toolsUsedInMessage.includes(tool)) {
                    toolsUsedInMessage.push(tool);
                  }
                });
                get().setActiveTools(Array.from(activeToolsSet));
              }

              // Update message capacity if provided
              if (progress.messageCapacity) {
                set({ messageCapacity: progress.messageCapacity });
              }

              // Detect text and tool-marker changes independently. Both can
              // arrive without the other: a tool_end fires with no new tokens
              // (markers change, text unchanged); a long tool chain fires
              // before any text (markers change, no text yet).
              let textChanged = false;
              if (progress.streamedContent && progress.streamedContent !== streamedText) {
                streamedText = progress.streamedContent;
                textChanged = true;

                // Set stage to STREAMING when content starts streaming
                if (streamedText.length > 0 && get().currentResponse?.stage === ResponseStage.WAITING) {
                  setResponseStage(ResponseStage.STREAMING, 'Streaming response...');
                  recordFirstToken(chatSpanRef);
                }
              }

              const markerSignature = toolMarkers
                .map(m => `${m.runId || `${m.tool}:${m.position}`}:${m.success ?? 'pending'}:${m.message ?? ''}`)
                .join('|');
              const markersChanged = markerSignature !== lastMarkerSignature;
              if (markersChanged) {
                lastMarkerSignature = markerSignature;
              }

              if ((textChanged || markersChanged) && (streamedText.length > 0 || toolMarkers.length > 0)) {
                // When node tagging is active (node === 'conclusion_render'), only show conclusionContent in the answer body.
                // Pre-conclusion thoughts/reasoning are owned by the WorkingSection pill.
                const displayContent = (progress.nodeTagged && typeof progress.conclusionContent === 'string')
                  ? progress.conclusionContent
                  : streamedText;
                const { content: cleanedContent, suggestions } = parseSuggestedResponses(displayContent);

                updateResponseProgress({
                  ...progress,
                  stage: streamedText.length > 0
                    ? ResponseStage.STREAMING
                    : (get().currentResponse?.stage || ResponseStage.WAITING),
                  toolMarkers: [...toolMarkers],
                  streamedContent: cleanedContent,
                });

                // Materialize the assistant bubble on the first tool OR the
                // first text token. The bubble's WorkingSection owns all
                // pre-conclusion display, so we want it visible from the
                // moment work begins.
                if (streamedText.length > 0 || toolMarkers.length > 0 || streamingMessageId) {
                  syncStreamingMessage(cleanedContent, suggestions);
                }
              }
            },
            displayText,
            get().slackSimulation?.enabled ? get().slackSimulation : undefined,
            // Auto-route (3-way) from the ACCOUNT's setting (synced from /me): the account is
            // Classic, Code, or Code Simple. Simple wins over Code if both flags are set. The
            // backend re-gates server-side either way.
            get().codeModeSimpleEnabled
              ? 'code_simple'
              : get().codeModeEnabled
                ? 'code'
                : 'classic',
            // Read-only ask-card marker — true only on a per-card instance store
            // (seeded by sharedConversationStores). The global panel store leaves it
            // false, so a "Continue in chat" continuation of the same conversation
            // sends no flag and runs full Leo. Off-by-default → no sync-chat change.
            get().askCardReadOnly,
            { attempt },
          );
        };

        // Sentry span reference for chat turn tracing
        let chatSpanRef: import('@sentry/react').Span | undefined;

        // The transport owns bounded recovery and preserves the resume cursor.
        // A second loop here would reset that budget and resubmit the message.
        await traceChatTurn(
          'chat.send_message',
          {
            conversationId: activeConversationId,
            isStreaming: true,
            isRetry: isRetry,
          },
          async (span) => {
            chatSpanRef = span;
            response = await performStreamRequest();
          },
        );

        // A tracing integration must not swallow the operation's response.
        if (!response) {
          throw new Error('Failed to get response');
        }

        // POST-STREAM GUARD: If the conversation changed while we were
        // streaming (workspace switch, new chat, etc.), discard the result
        // entirely. The worker will still save the response to S3 so the
        // conversation is available in history when the user returns.
        if (isStaleStream()) {
          console.log('🛑 [chatbotStore] Conversation changed during stream, discarding results');
          set({ currentResponse: null, responseStage: ResponseStage.IDLE });
          return;
        }

        // Flush any pending throttled message update
        if (throttleState.pendingUpdate) {
          throttleState.pendingUpdate();
          throttleState.pendingUpdate = null;
        }

        // Record first-conversation checklist event early — before any early-return
        // paths (questionnaire, continuation) that would skip the main completion block.
        // (History cache insert happened at send-time in _handleRealResponse.)
        if (isNewConversation) {
          const finalConversationId = response.conversationId || activeConversationId;
          posthog.capture(EVENTS.CONVERSATION.STARTED, { conversation_id: finalConversationId });

          // Persist the conversationId now so it's available for subsequent messages
          setConversationId(finalConversationId);
        }

        // Check if this is a structured questionnaire response
        // The agent paused to collect structured input — render the questionnaire UI
        if (response.metadata?.requires_questionnaire && response.metadata?.questionnaire_data) {
          console.log('📋 [chatbotStore] Questionnaire response detected — rendering questionnaire UI');
          const questionnaireData = response.metadata.questionnaire_data;

          // The interrupt fires BEFORE the conclusion node runs, so the response
          // body contains only Leo's pre-questionnaire reasoning — no <conclusion>
          // wrapper. Route that text into thinkingContent (the working pill) and
          // leave structuredContent.response empty so parseMessageContent doesn't
          // surface it as mainContent. The questionnaire wizard is the only
          // thing that should surface outside the pill in this state.
          const preQuestionnaireText = typeof response.content === 'string' ? response.content : '';
          // Capture WHERE in the pill's chronological order the wizard sits.
          // After the user answers, the resume stream's pre-conclusion content
          // gets appended to thinkingContent — splitting at this offset gives
          // us [pre-questionnaire tools] → [wizard] → [post-answer tools].
          const structuredContent: import('./types').StructuredContent = {
            response: '',
            requires_questionnaire: true,
            questionnaire_data: questionnaireData,
            questionnaire_marker_position: preQuestionnaireText.length,
          };

          if (streamingMessageId) {
            const { messages: msgs } = get();
            const msgIndex = msgs.findIndex(m => m.id === streamingMessageId);
            if (msgIndex !== -1) {
              const updated = [...msgs];
              updated[msgIndex] = {
                ...updated[msgIndex],
                content: structuredContent,
                thinkingContent: preQuestionnaireText,
              };
              set({ messages: updated });
            }
          } else {
            addMessage({
              type: MessageType.ASSISTANT,
              content: structuredContent,
              status: MessageStatus.SENT,
              thinkingContent: preQuestionnaireText,
            });
          }

          // Set response to idle — the questionnaire wizard will handle next steps
          get().setActiveTools([]);
          setResponseStage(ResponseStage.IDLE, 'Waiting for questionnaire');
          setTimeout(() => {
            set({ currentResponse: null, responseStage: ResponseStage.IDLE });
          }, 100);
          return;
        }

        // Check if this is a continuation response - if so, skip message handling
        // The conversation was already loaded by handleContinuationComplete and
        // the retry message will be sent separately
        if (response.metadata?.isContinuation) {
          console.log('📂 [chatbotStore] Continuation response detected - skipping message add');
          // The conversation and messages were already loaded by handleContinuationComplete
          // Just clear the state and let the retry message flow handle the rest
          get().setActiveTools([]);
          setResponseStage(ResponseStage.IDLE, 'Complete');
          setTimeout(() => {
            set({ currentResponse: null, responseStage: ResponseStage.IDLE });
          }, 100);
          return; // Early return - don't add any message
        }

        // Parse <suggested_actions> + split <conclusion> from thinking content.
        // Shared canonical parser — also used by the questionnaire-resume path
        // below. Keeping a single implementation prevents the two paths from
        // drifting (which was the cause of post-questionnaire messages
        // rendering without action buttons in production).
        const { content: finalCleanedContent, suggestions: finalSuggestions, thinkingContent } =
          parseAgentFinalContent(
            typeof response.content === 'string' ? response.content : ''
          );

        // Update final message with tools used, markers, and suggested responses
        if (streamingMessageId) {
          const { messages } = get();
          const messageIndex = messages.findIndex(m => m.id === streamingMessageId);
          if (messageIndex !== -1) {
            const updated = [...messages];
            updated[messageIndex] = {
              ...updated[messageIndex],
              content: finalCleanedContent || response.content,
              toolsUsed: toolsUsedInMessage.length > 0 ? toolsUsedInMessage : undefined,
              toolMarkers: toolMarkers.length > 0 ? toolMarkers : undefined,
              suggestedResponses: finalSuggestions.length > 0 ? finalSuggestions : (response.suggestedResponses || updated[messageIndex].suggestedResponses || undefined),
              thinkingContent,
            };
            set({ messages: updated });
          }
        } else {
          addMessage({
            type: MessageType.ASSISTANT,
            content: finalCleanedContent || response.content,
            status: MessageStatus.SENT,
            toolsUsed: toolsUsedInMessage.length > 0 ? toolsUsedInMessage : undefined,
            toolMarkers: toolMarkers.length > 0 ? toolMarkers : undefined,
            suggestedResponses: finalSuggestions.length > 0 ? finalSuggestions : (response.suggestedResponses || undefined),
            thinkingContent,
          });
        }

        // Persist conversationId if the backend returned a different one than we generated.
        // (New-conversation persistence is handled in the early block above.)
        const finalConversationId = response.conversationId || activeConversationId;
        if (!isNewConversation && response.conversationId && response.conversationId !== activeConversationId) {
          setConversationId(finalConversationId);
        }

        setChatContext({ conversationId: finalConversationId });
        recordStreamComplete(chatSpanRef);
        addChatBreadcrumb('message_complete', {
          conversationId: finalConversationId,
          toolsUsed: toolsUsedInMessage,
        });

        // Code Mode streams one `execute_code` marker around its internal
        // primitives, so the client cannot distinguish create_monitor from a
        // preference/layout write. Invalidating these snapshot-only queries is
        // intentionally cheap and makes every supported Tune path immediate.
        const codeModeMayHaveUpdatedDashboardConfig =
          toolsUsedInMessage.includes('execute_code');
        const leadAttributionPreferenceUpdated = toolsUsedInMessage.includes(
          'update_lead_attribution_preference',
        );
        if (
          codeModeMayHaveUpdatedDashboardConfig ||
          leadAttributionPreferenceUpdated ||
          toolsUsedInMessage.includes('update_executive_overview_layout')
        ) {
          void queryClient.invalidateQueries({
            queryKey: executiveOverviewQueryKeys.all,
          });
        }
        if (
          codeModeMayHaveUpdatedDashboardConfig ||
          toolsUsedInMessage.some((tool) =>
            ['create_monitor', 'update_monitor', 'refresh_monitor'].includes(
              tool,
            ),
          )
        ) {
          void queryClient.invalidateQueries({
            queryKey: monitorQueryKeys.all,
          });
        }
        if (
          codeModeMayHaveUpdatedDashboardConfig ||
          leadAttributionPreferenceUpdated
        ) {
          void queryClient.invalidateQueries({
            queryKey: leadAttributionQueryKeys.all,
          });
        }
        if (
          codeModeMayHaveUpdatedDashboardConfig ||
          toolsUsedInMessage.some((tool) =>
            ['send_user_message', 'create_memo', 'update_memo', 'archive_memo'].includes(tool),
          )
        ) {
          void queryClient.invalidateQueries({ queryKey: feedQueryKeys.all });
        }
        // An edit to analytics/view.txt saves when its block ends and leaves no <view> in the reply.
        if (codeModeMayHaveUpdatedDashboardConfig) {
          void queryClient.invalidateQueries({
            queryKey: dataQueryKeys.all,
            predicate: ({ queryKey }) => queryKey[2] === 'analytics',
          });
        }
        refreshAnalyticsIfViewed(finalCleanedContent || (typeof response.content === 'string' ? response.content : ''));

        // Clear active tools and response stage
        get().setActiveTools([]);
        setResponseStage(ResponseStage.IDLE, 'Complete');
        setTimeout(() => {
          set({ currentResponse: null, responseStage: ResponseStage.IDLE });
        }, 100);
      } catch (error) {
        // Preparation and transport share one once-only receipt owner. Report
        // before Sentry/UI callbacks, which may themselves be the failing code.
        attempt.fail(error);
        // Log technical details for debugging, but don't show to user
        console.error('Real API response error:', error);

        captureChatError(error, {
          conversationId: activeConversationId,
          isRetry,
        });

        // POST-STREAM GUARD (error path): same as above — if the
        // conversation changed, silently discard the error.
        if (isStaleStream()) {
          console.log('🛑 [chatbotStore] Conversation changed during stream (error path), discarding');
          set({ currentResponse: null, responseStage: ResponseStage.IDLE });
          return;
        }

        // The executive refused the steer (503 unavailable, or off): say so as-is.
        // Not the calm "trouble connecting" banner, and no Retry — nothing falls back.
        const executiveRefusal = isExecutiveConversationId(activeConversationId) ? executiveSendRefusal(error) : null;
        if (executiveRefusal) {
          set({ executiveNotice: executiveRefusal, currentResponse: null, responseStage: ResponseStage.IDLE });
          return;
        }

        // If stream was interrupted, the streamInterrupted UI is already handling it
        // Don't show the general error banner - just clean up state
        const currentStreamInterrupted = get().streamInterrupted;
        if (currentStreamInterrupted.isInterrupted) {
          console.log('🔄 [chatbotStore] Stream interrupted - letting streamInterrupted UI handle it');
          setTimeout(() => {
            set({ currentResponse: null, responseStage: ResponseStage.IDLE });
          }, 100);
          return;
        }

        // Check for specific error codes from backend
        const errorCode = (error as any)?.code;
        const isSubscriptionError = errorCode === 'SUBSCRIPTION_REQUIRED';

        // If subscription error, dispatch event to show subscription modal
        if (isSubscriptionError) {
          console.log('🔔 [chatbotStore] Subscription required - dispatching event to show modal');
          window.dispatchEvent(new CustomEvent('subscription-required'));

          // Roll back the optimistic history entry — the send was rejected
          // before the agent ran, so the conversation was never created.
          if (isNewConversation) {
            updateChatHistoryCache(queryClient, { kind: 'remove', conversationId: activeConversationId });
          }

          // Don't show error in chat - the modal will handle it
          setResponseStage(ResponseStage.IDLE, '');
          setTimeout(() => {
            set({ currentResponse: null, responseStage: ResponseStage.IDLE });
          }, 100);
          return;
        }

        // An unauthenticated refusal is not a Leo failure and Retry cannot
        // cure it — see handleSessionExpired.
        const deliveryContext = deliveryErrorContext(error);
        if (isSessionExpiredError(error)) {
          get().handleSessionExpired(content, uploadedImages, isHidden, displayText, {
            sendId: attempt.sendId,
            acknowledged: deliveryContext?.acknowledged ?? false,
            postMayHaveLanded: deliveryContext?.postMayHaveLanded ?? false,
            newConversation: isNewConversation,
          });
          return;
        }

        // For other errors, use the calm stream interrupted UI
        const userFriendlyMessage = 'Having trouble connecting. Please try again in a moment.';

        get().handleStreamInterrupted(
          '',
          userFriendlyMessage,
          true,
          content,
          uploadedImages,
          isHidden,
          displayText,
          undefined,
          { sendId: attempt.sendId },
        );
      }
    },

      setHistorySearchText: (text: string) => {
        set({ historySearchText: text });
      },

      setHistoryOriginFilter: (filter) => {
        set({ historyOriginFilter: filter });
      },

      setHistoryStartedByFilter: (filter) => {
        set({ historyStartedByFilter: filter });
      },

      loadConversationById: async (conversationId: string, opts?: { background?: boolean; isCurrentScope?: () => boolean; keepLiveTail?: boolean; historyCursor?: string }) => {
        if (opts?.isCurrentScope && !opts.isCurrentScope()) return;
        const executive = isExecutiveConversationId(conversationId);
        const historyLoadId = executive ? get().executiveHistoryLoadId + 1 : 0;
        if (executive) set({ executiveHistoryLoadId: historyLoadId,
          executiveHistoryLoading: Boolean(opts?.historyCursor) || get().executiveHistoryViewingOlder });
        // Capture ended event for the conversation being navigated away from
        const sessionCurrent = captureExecutiveSession(conversationId);
        const previousConversationId = get().conversationId;
        if (previousConversationId && previousConversationId !== conversationId) {
          _captureConversationEnded(previousConversationId);
        }
        console.log('📂 [chatbotStore] loadConversationById called:', conversationId);
        setChatContext({ conversationId });
        addChatBreadcrumb('load_conversation', { conversationId });

        const isCurrentlyStreaming =
          get().conversationId === conversationId &&
          (get().responseStage !== ResponseStage.IDLE || chatbotApi.hasActiveStream(conversationId));

        if (previousConversationId !== conversationId) set({ inbox: [] });
        const executiveRefresh = isExecutiveConversationId(conversationId) && previousConversationId === conversationId;
        if (!isCurrentlyStreaming && !opts?.background && !executiveRefresh) {
          set({
            conversationId,
            conversationLoading: true,
            messages: [], // Clear messages immediately to show skeleton
            title: null,
            showWelcomeScreen: false, // Ensure ChatbotMessages is rendered (not ChatbotWelcome)
            streamInterrupted: initialStreamInterruptedState, // Clear any interrupted state from previous conversation
            sseConnection: initialSSEConnectionState, // Reset SSE connection state
            error: null, // Clear any error state
            executionTodo: null,
          });
        } else {
          // A live stream owns the surface — hydrate silently in the
          // background. No skeleton over content that is actively rendering.
          set({ showWelcomeScreen: false, ...(executiveRefresh ? { conversationLoading: get().messages.length === 0 } : {}) });
        }
        // Initialize tracking for the loaded conversation
        _conversationStartedAt = Date.now();
        _conversationMessageCount = 0;
        try {
          const conversation = await NewConversationService.getById(conversationId, opts?.historyCursor);

          // Guard: user navigated away (new chat, different conversation) during fetch
          if (get().conversationId !== conversationId || !sessionCurrent() ||
              (executive && get().executiveHistoryLoadId !== historyLoadId) || (opts?.isCurrentScope && !opts.isCurrentScope())) {
            console.log('📂 [chatbotStore] Discarding stale conversation load:', conversationId);
            return;
          }
          console.log('📂 [chatbotStore] Raw conversation loaded:', {
            conversationId,
            messageCount: conversation.messages?.length || 0,
            metadata: conversation.metadata,
            firstMessagePreview: conversation.messages?.[0]?.message?.slice(0, 100) || 'N/A',
          });

          if (typeof conversation.title === 'string' && conversation.title.trim()) {
            set({ title: conversation.title.trim() });
          }

          // Parallel array: did the source message contain a <conclusion>
          // wrapper? Needed by the thought-fold pass below — we can't infer
          // it post-hoc from displayContent since both wrapped and unwrapped
          // strip to plain text. Index-aligned with the mapped messages
          // array; only updated during .map (not .filter).
          const hadConclusionTag: boolean[] = [];

          // Transform the conversation data to match our message format
          const messages: ChatbotMessage[] = conversation.messages?.filter((msg: any) => {
            // Filter out metadata messages - prioritize direct message field
            const content = msg.message ||
              (typeof msg.content === 'string' ? msg.content : msg.content?.response || msg.content?.message || msg.content?.text) ||
              msg.answer || msg.text || '';
            const isMetadata = content === 'START' ||
              content === 'END' ||
              content.startsWith('Conversation titled:') ||
              (msg.role === 'system' && !content.trim()) || // Only filter empty system messages
              (msg.role !== 'tool' && (!content || content.trim() === '')); // Allow empty content for tools

            // Debug logging for continuation conversations
            if (conversationId.includes('cont_') && msg.role === 'assistant') {
              console.log('📂 [chatbotStore] Assistant message in continuation:', {
                role: msg.role,
                contentPreview: content.slice(0, 100),
                isMetadata,
                willFilter: isMetadata,
              });
            }

            return !isMetadata;
          }).map((msg: any) => {

            // Handle different content structures - prioritize direct message field
            let content: string | any;

            if (msg.message) {
              // Direct message field (most common in new API)
              content = msg.message;
            } else if (typeof msg.content === 'string') {
              content = msg.content;
            } else if (msg.content && typeof msg.content === 'object') {
              // If content is an object, check for common properties
              if (msg.content.response) {
                content = msg.content; // Keep structured content for approval messages
              } else if (msg.content.message) {
                content = msg.content.message;
              } else if (msg.content.answer) {
                content = msg.content.answer; // Handle new API format
              } else if (msg.content.text) {
                content = msg.content.text;
              } else {
                // Fallback: stringify the object or use a default
                content = JSON.stringify(msg.content);
              }
            } else {
              // Try other direct properties
              content = msg.answer || msg.text || 'Unknown message content';
            }

            // Determine message type based on role field
            const messageType = msg.role === 'tool' ? MessageType.TOOL : (msg.role === 'user' ? MessageType.USER : MessageType.ASSISTANT);

            // Process images for user messages
            let images: any[] = [];
            if (messageType === MessageType.USER && msg.uploaded_images && Array.isArray(msg.uploaded_images)) {
              images = msg.uploaded_images.map((img: any) => ({
                file: null, // No file object for loaded images
                name: img.name || img.original_name, // Preserve filename for document display
                url: img.url || img.s3_url || img.image_url, // Use presigned URL if available
                uploaded: true,
                s3_url: img.s3_url || img.image_url,
                creative_id: img.creative_id || img.file_id,
                file_id: img.file_id,
                s3_path_to_thumbnail: img.s3_path_to_thumbnail,
                description: img.description, // Include description if available
              }));
            }

            const suggestedResponses = msg.suggested_responses ||
              (msg as any).suggestedResponses ||
              undefined;

            // Parse <conclusion> and <suggested_actions> for agent messages loaded from S3.
            // Tags are preserved in storage for in-context learning; strip for display.
            let displayContent = content;
            let thinkingContent: string | undefined;
            let hadConclusion = false;
            if (messageType === MessageType.ASSISTANT && typeof content === 'string') {
              // Route reload through the same canonical parser the live
              // finalize path uses, instead of a private greedy regex. It
              // selects the LAST <conclusion> as the reply and collapses
              // everything before it (incl. any draft conclusion + <triage>)
              // into thinkingContent — fixing the duplicate-conclusion leak on
              // reload (#4205/#4207).
              hadConclusion = /<conclusion\b[^>]*>/i.test(content);
              const parsed = parseAgentFinalContent(content);
              refreshAnalyticsIfViewed(parsed.content);
              displayContent = parsed.content;
              thinkingContent = parsed.thinkingContent;
            }
            // Display-only backend field for GPT-5.x reasoning summaries.
            // Persisted separately from `message` so the model doesn't see its
            // own past inner-monologue as history; merged in here so the
            // WorkingSection pill renders the same narration the live stream
            // showed. Legacy messages have no such field and fall through to
            // whatever parseAgentFinalContent recovered above.
            const persistedThinking = (msg as any).thinking_content;
            if (persistedThinking && typeof persistedThinking === 'string' && persistedThinking.trim()) {
              thinkingContent = thinkingContent
                ? `${persistedThinking}\n\n${thinkingContent}`
                : persistedThinking;
            }
            hadConclusionTag.push(hadConclusion);

            return {
              id: msg.id || generateId(),
              type: messageType,
              content: displayContent,
              timestamp: msg.timestamp ? new Date(msg.timestamp).getTime() : Date.now(),
              savedTimestamp: typeof msg.timestamp === 'string' ? msg.timestamp : undefined,
              status: MessageStatus.SENT,
              suggestedResponses: suggestedResponses, // Extract suggested responses from agent messages
              thinkingContent,
              images: images.length > 0 ? images : undefined,
              // Map tool specific fields
              tool_name: msg.tool_name,
              tool_args: msg.tool_args,
              tool_output: msg.tool_output,
              is_error: msg.is_error,
              tool_call_id: msg.tool_call_id,
              // Turn identity, hidden payload, its display line, and whether the
              // harness wrote it — one mapping the live chat renders by too.
              ...savedPresentation(msg),
            };
          }) || [];

          // Fold standalone "thought" assistant messages into the following
          // conclusion-wrapped message. Backend storage splits a single
          // streamed turn into [thought-asst, tool, tool, conclusion-asst];
          // streaming-time rendering keeps it as one bubble (thinkingContent
          // + content). Without this fold, reload shows the thought as a
          // separate bubble with the raw reasoning rendered as if it were
          // the answer — exactly what users saw after refreshing.
          //
          // A "thought" = assistant message with no <conclusion> wrapper
          // (hadConclusionTag[i] === false) that is followed — across any
          // number of tool messages and additional thought messages — by an
          // assistant message that DID have a wrapper. Thoughts get folded
          // into that conclusion message's thinkingContent in order, then
          // hidden. Tools are then attributed to the conclusion message by
          // the next pass, which walks back through TOOL messages.
          for (let i = 0; i < messages.length; i++) {
            if (messages[i].type !== MessageType.ASSISTANT) continue;
            if (hadConclusionTag[i]) continue;
            if (messages[i].hidden) continue;

            // Walk forward through tools + additional thoughts looking for a
            // conclusion-wrapped assistant message. Stop at a user message
            // (turn boundary) or end-of-conversation.
            let foundConclusion = -1;
            for (let j = i + 1; j < messages.length; j++) {
              if (crossesTurnBoundary(messages[i], messages[j])) break;
              if (messages[j].type === MessageType.TOOL) continue;
              if (messages[j].type === MessageType.ASSISTANT) {
                if (hadConclusionTag[j]) { foundConclusion = j; break; }
                continue; // Another thought — keep walking.
              }
              break; // User message: this turn doesn't have a conclusion.
            }
            if (foundConclusion === -1) continue;

            const thoughtText = typeof messages[i].content === 'string'
              ? (messages[i].content as string)
              : '';
            if (!thoughtText.trim()) {
              messages[i].hidden = true;
              continue;
            }
            const target = messages[foundConclusion];
            const existing = target.thinkingContent || '';
            target.thinkingContent = existing
              ? `${existing}\n\n${thoughtText}`
              : thoughtText;
            messages[i].hidden = true;
          }

          // Reconstruct toolMarkers on assistant messages from adjacent TOOL messages.
          // During streaming, toolMarkers are built in real-time from SSE events.
          // For loaded conversations, we synthesize them from the TOOL messages in the history
          // so that the same collapsed "N tools used" UI appears on reload.
          //
          // Backend message order: [thought(optional)] → [tools] → [agent response]
          // The thought message is often absent (LLM went straight to tools), so tools
          // typically precede the assistant response. We look BACKWARD from each assistant
          // message to collect the tool block that belongs to it.
          for (let i = 0; i < messages.length; i++) {
            if (messages[i].type !== MessageType.ASSISTANT) continue;
            const assistantMsg = messages[i];

            // Collect consecutive TOOL messages immediately before this assistant message
            let j = i - 1;
            while (j >= 0 && messages[j].type === MessageType.TOOL && !crossesTurnBoundary(assistantMsg, messages[j])) {
              j--;
            }
            // j now points to the last non-TOOL message before the tool block (or -1)
            const toolStartIndex = j + 1;
            if (toolStartIndex >= i) continue; // No tools before this assistant message

            const toolMarkers: Array<{ position: number; tool: string; message?: string; success?: boolean | null }> = [];
            for (let k = toolStartIndex; k < i; k++) {
              const toolMsg = messages[k];
              toolMarkers.push({
                position: 0, // All at start — renderContentWithTools groups same-position markers
                tool: toolMsg.tool_name || 'System Tool',
                message: toolMsg.tool_name ? `using ${toolMsg.tool_name}` : undefined,
                success: toolMsg.is_error ? false : true,
              });
              toolMsg.hidden = true; // Hide standalone TOOL card — now shown in the group
            }

            // The steps the server saved with the reply (`step_markers`) are what the
            // pill showed live; tool messages only stand in when there are none.
            if (toolMarkers.length > 0 && !assistantMsg.toolMarkers?.length) {
              assistantMsg.toolMarkers = toolMarkers;
            }
          }

          // Read capacity from S3 metadata (backend is source of truth)
          // Old conversations without metadata.capacity will show 0% until first message exchange
          const metadataCapacity = conversation.metadata?.capacity;
          const capacity = metadataCapacity ? {
            tokenCount: metadataCapacity.token_count || 0,
            capacityPercentage: metadataCapacity.capacity_percentage || 0,
            maxTokens: metadataCapacity.max_tokens || 200000,
            isAtLimit: (metadataCapacity.capacity_percentage || 0) >= 100
          } : {
            tokenCount: 0,
            capacityPercentage: 0,
            maxTokens: 200000,
            isAtLimit: false
          };

          const executionTodo = normalizeExecutionTodo(conversation.metadata?.execution_todo);

          console.log('📂 [chatbotStore] Messages after transformation:', {
            conversationId,
            finalMessageCount: messages.length,
            messageTypes: messages.map(m => m.type),
            firstMessagePreview: messages[0]?.content?.toString().slice(0, 100) || 'N/A',
          });

          // A live reader (send or attach) may have been building the current
          // turn's assistant bubble while this fetch ran. Merge the saved
          // history with that tail until its durable turn identity is saved.
          const liveReaderActive = !opts?.historyCursor &&
            get().conversationId === conversationId &&
            (Boolean(opts?.keepLiveTail) || get().responseStage !== ResponseStage.IDLE || chatbotApi.hasActiveStream(conversationId));
          if (executiveRefresh && messages.length === 0 && get().messages.length > 0) {
            set({ conversationLoading: false, executiveNotice: { kind: 'unavailable', text: 'The thread refresh returned no history. Showing the last loaded conversation.' } });
            return;
          }
          let mergedMessages = liveReaderActive
            ? mergeSnapshotWithLiveTail(get().messages, messages)
            : messages;

          if (executive && !opts?.historyCursor) mergedMessages = mergeExecutiveHistory(get().messages, mergedMessages);
          set({
            messages: mergedMessages,
            ...(executive ? { executiveHistoryNextCursor: typeof conversation.history_next_cursor === 'string'
              ? conversation.history_next_cursor : null, executiveHistoryViewingOlder: Boolean(opts?.historyCursor),
              executiveHistoryLoading: false } : {}),
            ...(executive && !opts?.historyCursor ? { inbox: dropSavedExecutiveInbox(get().inbox, mergedMessages) } : {}),
            ...(executiveRefresh ? { executiveNotice: null, error: null } : {}),
            conversationId,
            // Never flip to the welcome screen over a live stream.
            showWelcomeScreen: mergedMessages.length === 0 && !liveReaderActive,
            currentView: ChatbotView.CHAT,
            activeDetailRef: null,
            messageCapacity: capacity,
            // The live stream's todo (from tool events) is fresher than the
            // S3 snapshot's while a turn is in flight.
            executionTodo: liveReaderActive ? (get().executionTodo ?? executionTodo) : executionTodo,
            conversationLoading: false,
          });

          // Store conversationId in the chatbot store (persisted per workspace via Zustand persist)
          get().setConversationId(conversationId);
          // Mid-turn messages still waiting; ones the saved turn already holds are not shown twice.
          if (!opts?.historyCursor) void get().refreshInbox();
        } catch (error) {
          console.error('❌ [chatbotStore] Failed to load conversation:', error);

          // Guard: user navigated away during fetch — don't overwrite new state with error
          if (get().conversationId !== conversationId || !sessionCurrent() ||
              (executive && get().executiveHistoryLoadId !== historyLoadId) || (opts?.isCurrentScope && !opts.isCurrentScope())) {
            console.log('📂 [chatbotStore] Discarding stale conversation load error:', conversationId);
            return;
          }

          const denied = error instanceof Error && 'status' in error && (error.status === 401 || error.status === 403);
          if (denied) {
            chatbotApi.cancelRequest(conversationId);
            clearInterruptionTimers();
            set({ messages: [], inbox: [], currentResponse: null, responseStage: ResponseStage.IDLE,
              executionTodo: null, conversationLoading: false, showWelcomeScreen: false, executiveNotice: null,
              streamInterrupted: initialStreamInterruptedState, sseConnection: initialSSEConnectionState,
              error: { id: generateId(), code: 'CONVERSATION_ACCESS_DENIED', message: 'You no longer have access to this conversation.', timestamp: Date.now(), retryable: false } });
            return;
          }

          // Check if this is a "not found" error (conversation was deleted or doesn't exist)
          const errorMessage = error instanceof Error ? error.message : String(error);
          const isNotFoundError = errorMessage.toLowerCase().includes('not found') ||
                                  errorMessage.includes('404');
          
          if (executiveRefresh && isNotFoundError) {
            set({ conversationLoading: false, showWelcomeScreen: false,
              executiveNotice: { kind: 'unavailable', text: get().messages.length > 0
                ? 'The thread refresh could not find the conversation. Showing the last loaded history.'
                : 'The executive conversation could not be loaded. Refresh to try again.' } });
          } else if (isNotFoundError) {
            // Conversation no longer exists - clear state and show welcome screen
            // Setting conversationId to undefined is automatically persisted via Zustand (workspace-aware)
            console.log('📂 [chatbotStore] Conversation not found, resetting to welcome screen');
            set({
              conversationId: undefined,
              showWelcomeScreen: true,
              conversationLoading: false,
              messages: [],
              executionTodo: null,
            });
          } else {
            // Other errors (network, server, etc.) - show error state
            const loadError = {
              id: generateId(),
              code: 'CONVERSATION_LOAD_ERROR',
              message: 'Failed to load conversation',
              timestamp: Date.now(),
              retryable: true,
            };
            if (executiveRefresh) set({ error: loadError });
            else get().setError(loadError);
            set({ conversationLoading: false });
          }
        } finally {
          if (executive && sessionCurrent() && get().executiveHistoryLoadId === historyLoadId) set({ executiveHistoryLoading: false });
        }
      },

      // Approval actions
      approveTask: async (messageId: string) => {
        const { messages, conversationId, addMessage, updateMessage, setError } = get();

        // Find the message with approval data
        const messageIndex = messages.findIndex(m => m.id === messageId);
        if (messageIndex === -1) {
          throw new Error('Message not found');
        }

        const message = messages[messageIndex];
        const content = message.content;

        if (typeof content === 'string' || !content.requires_approval) {
          throw new Error('Message does not require approval');
        }

        const approvalData = content.approval_data;
        if (!approvalData || !conversationId) {
          throw new Error('Missing approval data or conversation ID');
        }

        try {
          // Build approval request payload
          const pending_tools = Array.isArray(approvalData.pending_tools)
            ? approvalData.pending_tools.map((t: any) => ({
              tool_name: t?.tool_name,
              tool_call_id: t?.tool_call_id,
              approval_text: t?.approval_text,
            }))
            : [];

          // Submit approval
          const response = await chatbotApi.submitApproval(
            conversationId,
            { pending_tools },
            true
          );

          // Mark message as approved
          updateMessage(messageId, {
            isDelegationApproved: true,
          });

          // Add response message if Lambda returns one
          if (response.content && response.content !== 'Task approved successfully.') {
            addMessage({
              type: MessageType.ASSISTANT,
              content: response.content,
              status: MessageStatus.SENT,
            });
          }
        } catch (error) {
          console.error('Approval error:', error);

          setError({
            id: generateId(),
            code: 'APPROVAL_ERROR',
            message: error instanceof Error
              ? error.message
              : 'Failed to approve task. Please try again.',
            timestamp: Date.now(),
            retryable: true,
          });

          throw error;
        }
      },

      cancelApproval: (_messageId: string) => {
        // Optional: Add logic to cancel approval if needed
      },

      // Questionnaire actions
      submitQuestionnaireAnswers: async (messageId: string, answers: import('./types').QuestionnaireAnswer[]) => {
        const { messages, conversationId, updateMessage, setError, setResponseStage, addMessage } = get();

        const messageIndex = messages.findIndex(m => m.id === messageId);
        if (messageIndex === -1) {
          throw new Error('Message not found');
        }

        const message = messages[messageIndex];
        const content = message.content;
        if (typeof content === 'string' || !content.requires_questionnaire) {
          throw new Error('Message does not have a questionnaire');
        }

        if (!conversationId) {
          throw new Error('No active conversation');
        }

        try {
          // Mark questionnaire as completed in the message
          updateMessage(messageId, {
            content: {
              ...content,
              questionnaire_completed: true,
              questionnaire_answers: answers,
            },
          });

          // Show loading state
          setResponseStage(ResponseStage.WAITING, 'Leo is processing your answers...');

          // Stream the agent's continuation response.
          //
          // CRITICAL: Do NOT create a second streaming message during resume.
          // The original questionnaire message is still isLastMessage, and
          // BotMessage's `isActive` branch already renders the live stream
          // from `currentResponse.streamedContent` into the original
          // message's pill. Adding a transient streaming message here would
          // produce TWO pills side-by-side (bug 2) — the live one on the new
          // message and the original questionnaire-record one beneath it.
          //
          // The final merge below folds the parsed result into the original
          // message's thinkingContent + structured response, so when the
          // stream ends the live pill (powered by currentResponse) seamlessly
          // becomes the persisted pill (powered by message.thinkingContent).
          // `streamingMsgId` is kept as `null` so the finalize fallback path
          // (msg vanished) won't try to mutate a non-existent message.
          const streamingMsgId: string | null = null;
          const response = await chatbotApi.submitQuestionnaireAnswersStream(
            conversationId,
            answers,
            (progress) => {
              // Update response progress (tool markers, streaming content)
              set({ currentResponse: progress });

              // Keep responseStage in sync so the spinner stays visible.
              // We set responseStage directly instead of calling setResponseStage(),
              // which would overwrite currentResponse and wipe streamedContent.
              if (progress.stage === ResponseStage.STREAMING || progress.stage === ResponseStage.TOOL_CALLING) {
                if (get().responseStage !== progress.stage) {
                  set({ responseStage: progress.stage });
                }
              }
              // No per-token message mutation — the original message picks up
              // currentResponse via BotMessage's isActive branch.
            },
          );

          // Check if the resume triggered another questionnaire (chained interrupts,
          // e.g., rundown: transition questionnaire → resume → nudge questionnaire).
          if (response.metadata?.requires_questionnaire && response.metadata?.questionnaire_data) {
            console.log('📋 [chatbotStore] Chained questionnaire detected after resume');
            const questionnaireData = response.metadata.questionnaire_data;

            // Same routing as the initial questionnaire arrival: pre-questionnaire
            // reasoning into thinkingContent (pill), response empty so the wizard
            // is the only thing surfaced outside the pill.
            const preQuestionnaireText = typeof response.content === 'string' ? response.content : '';
            const structuredContent: import('./types').StructuredContent = {
              response: '',
              requires_questionnaire: true,
              questionnaire_data: questionnaireData,
              questionnaire_marker_position: preQuestionnaireText.length,
            };

            if (streamingMsgId) {
              const { messages: msgs } = get();
              const idx = msgs.findIndex(m => m.id === streamingMsgId);
              if (idx !== -1) {
                const updated = [...msgs];
                updated[idx] = {
                  ...updated[idx],
                  content: structuredContent,
                  thinkingContent: preQuestionnaireText,
                };
                set({ messages: updated });
              }
            } else {
              addMessage({
                type: MessageType.ASSISTANT,
                content: structuredContent,
                status: MessageStatus.SENT,
                thinkingContent: preQuestionnaireText,
              });
            }

            get().setActiveTools([]);
            setResponseStage(ResponseStage.IDLE, 'Waiting for questionnaire');
            setTimeout(() => {
              set({ currentResponse: null, responseStage: ResponseStage.IDLE });
            }, 100);
            return;
          }

          // Final update — fold everything back into the ORIGINAL questionnaire
          // message so the wizard becomes a historical record inside the pill,
          // pre- and post-questionnaire reasoning accumulate in the same pill,
          // and the <conclusion> body lands outside the pill as the visible
          // reply. The user's expand/collapse preference on the pill is
          // preserved because the WorkingSection component instance persists.
          //
          // If a transient streaming message was created for the resume stream,
          // drop it — its content is being merged into the original message.
          if (response.content) {
            const rawContent = typeof response.content === 'string' ? response.content : '';
            const { content: cleaned, suggestions, thinkingContent: postQuestionnaireThinking } =
              parseAgentFinalContent(rawContent);
            refreshAnalyticsIfViewed(cleaned);

            const { messages: msgs } = get();
            const origIdx = msgs.findIndex(m => m.id === messageId);
            if (origIdx !== -1) {
              const original = msgs[origIdx];
              const priorThinking = original.thinkingContent || '';
              const mergedThinking = postQuestionnaireThinking
                ? (priorThinking
                    ? `${priorThinking}\n\n${postQuestionnaireThinking}`
                    : postQuestionnaireThinking)
                : priorThinking;

              // Merge tool markers from pre-questionnaire + post-questionnaire
              // streams. Post markers' positions are indices into the post text;
              // rebase them by `priorThinking.length + 2` (for the `\n\n`
              // separator inserted between the two thinking blocks above) so
              // they line up with the merged text.
              const priorToolMarkers = original.toolMarkers || [];
              const currentResp = get().currentResponse;
              const postToolMarkers = currentResp?.toolMarkers || [];
              const rebaseOffset = priorThinking ? priorThinking.length + 2 : 0;
              const rebasedPostMarkers = postToolMarkers.map(marker => ({
                ...marker,
                position: marker.position + rebaseOffset,
              }));
              const mergedToolMarkers = [...priorToolMarkers, ...rebasedPostMarkers];

              const priorStructured = typeof original.content === 'object' && original.content
                ? (original.content as import('./types').StructuredContent)
                : null;

              // Build the merged content: conclusion body goes outside the pill,
              // questionnaire data stays as record. If parseAgentFinalContent
              // returned a string conclusion, wrap it back into structured form
              // so the questionnaire fields survive.
              const mergedStructured: import('./types').StructuredContent = {
                ...(priorStructured || {}),
                response: cleaned || (typeof response.content === 'string' ? response.content : ''),
                requires_questionnaire: true,
                questionnaire_completed: true,
                questionnaire_answers: answers,
                // questionnaire_data preserved via spread from priorStructured
              };

              const updated = msgs
                .filter(m => !(streamingMsgId && m.id === streamingMsgId))
                .map(m => m.id === messageId
                  ? {
                      ...m,
                      content: mergedStructured,
                      thinkingContent: mergedThinking,
                      toolMarkers: mergedToolMarkers.length > 0
                        ? mergedToolMarkers
                        : m.toolMarkers,
                      suggestedResponses: suggestions.length > 0
                        ? suggestions
                        : m.suggestedResponses,
                    }
                  : m);
              set({ messages: updated });
            } else if (streamingMsgId) {
              // Fallback — original message vanished somehow; finalize the
              // streaming message as best-effort.
              const idx = msgs.findIndex(m => m.id === streamingMsgId);
              if (idx !== -1) {
                const updated = [...msgs];
                updated[idx] = {
                  ...updated[idx],
                  content: cleaned || response.content,
                  suggestedResponses: suggestions.length > 0 ? suggestions : updated[idx].suggestedResponses,
                  thinkingContent: postQuestionnaireThinking || updated[idx].thinkingContent,
                };
                set({ messages: updated });
              }
            } else {
              addMessage({
                type: MessageType.ASSISTANT,
                content: cleaned || response.content,
                status: MessageStatus.SENT,
                suggestedResponses: suggestions.length > 0 ? suggestions : undefined,
                thinkingContent: postQuestionnaireThinking,
              });
            }
          }

          setResponseStage(ResponseStage.IDLE, 'Complete');
          setTimeout(() => {
            set({ currentResponse: null, responseStage: ResponseStage.IDLE });
          }, 100);
        } catch (error) {
          console.error('Questionnaire submission error:', error);
          setResponseStage(ResponseStage.IDLE);

          // A 401 that survived the fetch's session refresh: retrying sends the
          // same refused request, so say what happened instead.
          const sessionExpired = isSessionExpiredError(error);
          setError({
            id: generateId(),
            code: sessionExpired ? 'SESSION_EXPIRED' : 'QUESTIONNAIRE_ERROR',
            message: sessionExpired
              ? 'Your session expired. Refresh the page to reconnect, then answer again.'
              : error instanceof Error
                ? error.message
                : 'Failed to submit questionnaire. Please try again.',
            timestamp: Date.now(),
            retryable: !sessionExpired,
          });

          throw error;
        }
      },

      // Side modal actions
      openAssetHubModal: () => {
        set({ isAssetHubModalOpen: true });
      },

      closeAssetHubModal: () => {
        set({ isAssetHubModalOpen: false });
      },

      // Slack simulation — internal-user toggle that injects SLACK_CONTEXT
      // triggers server-side without a real Slack channel.
      setSlackSimulation: (next) => {
        const current = get().slackSimulation || { enabled: false, async: false };
        const merged = { ...current, ...next };
        set({ slackSimulation: merged });
        // Fan out to every open per-chat store so an in-flight tab picks up the
        // toggle for its next send. New tabs seed from global at creation time.
        forEachSharedConversationStore((store) => {
          store.setState({ slackSimulation: merged });
        });
      },

      // Per-account Code Mode opt-in, synced from /me by ChatbotInput. When true,
      // new conversations auto-route to code mode even for a non-internal user.
      setCodeModeEnabled: (next) => {
        set({ codeModeEnabled: next });
        forEachSharedConversationStore((store) => {
          store.setState({ codeModeEnabled: next });
        });
      },

      // Per-account Code Mode SIMPLE opt-in, synced from /me by ChatbotInput.
      setCodeModeSimpleEnabled: (next) => {
        set({ codeModeSimpleEnabled: next });
        forEachSharedConversationStore((store) => {
          store.setState({ codeModeSimpleEnabled: next });
        });
      },

      // Continuation actions for context overflow recovery
      handleOverflowRecoveryStarted: (originalConversationId: string) => {
        console.log('🔄 [chatbotStore] Overflow recovery started for conversation:', originalConversationId);

        // Check if we're already in recovery state to prevent duplicate processing
        const currentState = get().continuation;
        if (currentState.isRecovering && currentState.originalConversationId === originalConversationId) {
          console.log('⚠️ [chatbotStore] Already in recovery state for this conversation, skipping duplicate');
          return;
        }

        set({
          continuation: {
            isRecovering: true,
            originalConversationId,
            retryMessage: null,
            recoveryStartTime: Date.now(),
          },
          responseStage: ResponseStage.WAITING,
          currentResponse: {
            requestId: `recovery_${Date.now()}`,
            stage: ResponseStage.WAITING,
            progress: 50,
            startTime: Date.now(),
            estimatedDuration: 40000, // ~40 seconds for summary generation
            currentActivity: 'Creating a summary of our conversation...',
            canCancel: false,
          },
        });

        // Show single, positive toast for expectation management
        // This is the only toast for the entire overflow recovery flow
        // Keep it visible until recovery completes (dismissed in handleContinuationComplete)
        console.log('🔔 [chatbotStore] Showing overflow-recovery toast');
        toast.success('📝 We\'re summarizing this conversation and moving you to a new chat. Please hold on for about 30-40 seconds.', {
          autoClose: false, // Don't auto-close - we'll dismiss it when complete
          toastId: 'overflow-recovery', // Single toast ID for entire flow
        });
      },

      handleContinuationComplete: async (
        newConversationId: string,
        retryMessage: string,
        continuedFrom: string,
        _continuationChain: string[]
      ) => {
        console.log('✅ [chatbotStore] Continuation complete:', {
          newConversationId,
          retryMessage: retryMessage.slice(0, 100) + (retryMessage.length > 100 ? '...' : ''),
          continuedFrom,
        });

        const { loadConversationById, sendMessage, setConversationId } = get();

        try {
          // 1. Update conversation ID to the new continuation
          setConversationId(newConversationId);

          // 2. Load the new conversation (which contains the summary as first message)
          console.log('📂 [chatbotStore] Loading new conversation:', newConversationId);
          await loadConversationById(newConversationId);
          console.log('📂 [chatbotStore] New conversation loaded, messages:', get().messages.length);

          // 3. Clear the continuation state
          set({
            continuation: initialContinuationState,
            responseStage: ResponseStage.IDLE,
            currentResponse: null,
          });

          // 4. Dismiss the recovery toast now that we're done
          toast.dismiss('overflow-recovery');
          console.log('✅ [chatbotStore] Continuation complete - conversation loaded');

          // 5. Invalidate React Query cache and force refetch
          // Using refetchType: 'all' ensures refetch happens even if history panel isn't visible
          console.log('📋 [chatbotStore] Invalidating and refetching chat history cache');
          await queryClient.invalidateQueries({
            queryKey: chatHistoryQueryKeys.all,
            refetchType: 'all',  // Force refetch even for inactive queries
          });
          // Also update Zustand store for any components that use it directly
          get().loadConversationHistory();

          // 6. Auto-retry the failed message after a brief delay
          // The retry is just a normal agent call - no special timeout needed
          setTimeout(async () => {
            console.log('🔄 [chatbotStore] Auto-retrying message in new conversation');

            try {
              await sendMessage(retryMessage);
              console.log('✅ [chatbotStore] Retry message sent successfully');
            } catch (retryError) {
              console.error('❌ [chatbotStore] Failed to retry message:', retryError);

              // Reset loading state and show error
              set({
                responseStage: ResponseStage.IDLE,
                currentResponse: null,
                error: {
                  id: `retry_error_${Date.now()}`,
                  code: 'RETRY_ERROR',
                  message: retryError instanceof Error
                    ? retryError.message
                    : 'Failed to retry message. Please try sending your message again.',
                  timestamp: Date.now(),
                  retryable: true,
                  originalRequest: retryMessage,
                },
              });

              toast.error('Failed to retry your message. Please try again.', {
                autoClose: 5000,
                toastId: 'retry-error',
              });
            }
          }, 500);
        } catch (error) {
          console.error('❌ [chatbotStore] Failed to complete continuation:', error);
          get().handleOverflowError('Failed to load the continuation conversation. Please try refreshing.');
        }
      },

      handleOverflowError: (errorMessage: string) => {
        console.error('❌ [chatbotStore] Overflow recovery failed:', errorMessage);

        // Dismiss the recovery toast before showing error
        toast.dismiss('overflow-recovery');

        set({
          continuation: initialContinuationState,
          responseStage: ResponseStage.ERROR,
          currentResponse: null,
          error: {
            id: `overflow_error_${Date.now()}`,
            code: 'OVERFLOW_UNRECOVERABLE',
            message: errorMessage,
            timestamp: Date.now(),
            retryable: false,
          },
        });

        toast.error(errorMessage, {
          autoClose: 10000,
          toastId: 'overflow-error',
        });
      },

      clearContinuationState: () => {
        set({
          continuation: initialContinuationState,
        });
      },

      // Stream interrupted actions for retry/resume
      handleStreamInterrupted: (
        partialResponse: string,
        error: string,
        isRetryable: boolean,
        originalMessage: string,
        uploadedImages?: UploadedImage[],
        isHidden?: boolean,
        displayText?: string,
        turnId?: string,
        delivery?: InterruptionDelivery,
      ) => {
        console.log('❌ [chatbotStore] Stream interrupted:', {
          partialResponseLength: partialResponse.length,
          error,
          isRetryable,
          originalMessageLength: originalMessage.length,
        });

        captureChatWarning('Stream interrupted', {
          error,
          isRetryable,
          partialResponseLength: partialResponse.length,
        });

        const { conversationId } = get();

        clearInterruptionTimers();
        const nextInterruptedState: StreamInterruptedState = {
          isInterrupted: true,
          turnId,
          partialResponse,
          error,
          isRetryable,
          originalMessage: continuationRecoveryContext(get().messages, turnId) ? '' : originalMessage,
          uploadedImages: normalizeUploadedImages(uploadedImages),
          isHidden,
          displayText,
          interruptedAt: Date.now(),
          conversationId: conversationId || null,
          source: 'llm',
          phase: isRetryable ? 'retry' : 'blocked',
          disconnectStartedAt: null,
          lastReconnectAttemptAt: null,
          sendId: delivery?.sendId,
        };
        set({
          streamInterrupted: {
            ...nextInterruptedState,
          },
          // Keep responseStage at IDLE so the input remains functional
          // The gray retry banner is controlled by streamInterrupted.isInterrupted
          responseStage: ResponseStage.IDLE,
          currentResponse: null,
          // Clear any existing error state - the streamInterrupted UI handles this case
          error: null,
        });
        assertInterruptionState(nextInterruptedState, 'handleStreamInterrupted');

        // A turn whose checkpoint the server vouched for — a declared
        // interrupt, a resumable worker exit such as the soft limit — rejoins
        // once on its own. The person already waited; the banner is the
        // second failure, not the first. A transport failure never does: its
        // POST may have landed or its worker may be redelivered, and a rejoin
        // then would run the turn twice.
        const canRejoin = Boolean(nextInterruptedState.originalMessage)
          || Boolean(continuationRecoveryContext(get().messages, turnId));
        if (isRetryable && delivery?.autoResume && canRejoin && claimAutoResume(delivery.sendId)) {
          // After the in-flight send unwinds. Calling retry here would start a
          // second send inside the one that just failed.
          setTimeout(() => { void get().retryInterruptedMessage(); }, 0);
          return;
        }

        // Show a calm, non-alarming toast (8 seconds visibility)
        toast.info("Leo encountered an error. You can retry below.", {
          autoClose: 8000,
          toastId: 'stream-interrupted',
        });
      },

      handleSessionExpired: (
        originalMessage: string,
        uploadedImages?: UploadedImage[],
        isHidden?: boolean,
        displayText?: string,
        delivery?: SessionExpiryDelivery,
      ) => {
        // The "Leo encountered an error — retry" framing names the wrong party
        // and offers an action that re-sends the same request with the same
        // missing cookie. A reload re-runs the auth bootstrap; nothing else will.
        console.warn('🔒 [chatbotStore] Send refused as unauthenticated — session expired');
        captureChatWarning('Session expired on send', { status: 401 });

        const { conversationId } = get();
        const images = normalizeUploadedImages(uploadedImages);
        // Refused after the server accepted the send: the turn exists, and the
        // reload reattaches to it. Parking would replay the message a second
        // time. Otherwise keep it across the reload so they do not retype.
        const sessionRecovery: SessionRecovery = delivery?.acknowledged
          ? 'reattach'
          : !delivery?.postMayHaveLanded && imagesSurviveReload(images) ? 'replay' : 'restore';
        if (sessionRecovery !== 'reattach' && originalMessage && typeof sessionStorage !== 'undefined') {
          const pins = currentTenantPins();
          parkChatSend({
            conversationId: conversationId ?? null,
            startsConversation: Boolean(delivery?.newConversation) || startsConversation(get().messages),
            message: originalMessage,
            displayText,
            isHidden,
            images,
            sendId: delivery?.sendId,
            autoSend: sessionRecovery === 'replay',
            ...pins,
          }, sessionStorage, Date.now());
        }
        clearInterruptionTimers();
        const nextInterruptedState: StreamInterruptedState = {
          isInterrupted: true,
          partialResponse: '',
          error: 'Your session expired.',
          isRetryable: false,
          originalMessage,
          uploadedImages: images,
          isHidden,
          displayText,
          interruptedAt: Date.now(),
          conversationId: conversationId || null,
          source: 'auth',
          phase: 'session_expired',
          disconnectStartedAt: null,
          lastReconnectAttemptAt: null,
          sendId: delivery?.sendId,
          sessionRecovery,
        };
        set({
          streamInterrupted: nextInterruptedState,
          responseStage: ResponseStage.IDLE,
          currentResponse: null,
          error: null,
        });
        assertInterruptionState(nextInterruptedState, 'handleSessionExpired');

        toast.info(SESSION_RECOVERY_TOAST[sessionRecovery], { autoClose: 8000, toastId: 'stream-interrupted' });
      },

      replayParkedSend: async () => {
        if (typeof sessionStorage === 'undefined') return;
        const nowMs = Date.now();
        const parked = readParkedChatSend(sessionStorage, nowMs);
        if (!parked) return;
        const state = get();
        const target = parkedSendTarget(parked, {
          conversationId: state.conversationId,
          hasMessages: state.messages.length > 0,
          idle: state.responseStage === ResponseStage.IDLE
            && !state.conversationLoading
            && !state.streamInterrupted.isInterrupted,
          ...currentTenantPins(),
        });
        if (target === 'wait') return;
        const taken = takeParkedChatSend(sessionStorage, nowMs);
        if (!taken || target === 'drop') return;
        // A duplicated tab carries its own copy of the park; one tab sends it.
        if (typeof localStorage !== 'undefined' && !claimParkedSend(localStorage, taken.sendId, nowMs)) return;
        if (target === 'restore') {
          // It cannot go as typed — a file did not survive the reload, or an
          // earlier POST may already have reached Leo. Hand the words back
          // rather than make them retype them. A hidden send's text was never
          // theirs to see.
          if (taken.isHidden || get().inputValue) return;
          set({ inputValue: taken.message });
          toast.info(
            imagesSurviveReload(taken.images)
              ? "Your message is back in the box. Send it if Leo didn't pick it up."
              : 'Your message is back in the box. Attach the file again and send.',
            { autoClose: 8000, toastId: 'parked-send-restored' },
          );
          return;
        }
        await get().sendMessage(
          taken.message,
          taken.images.length ? taken.images : undefined,
          taken.isHidden,
          taken.displayText,
          undefined,
          taken.sendId,
        );
      },

      retryInterruptedMessage: async () => {
        const { streamInterrupted, conversationId, setResponseStage } = get();
        const recovery = continuationRecoveryContext(get().messages, streamInterrupted.turnId);
        if (recovery) {
          await get().recoverParentContinuation(recovery.turnId);
          return;
        }

        if (!streamInterrupted.isInterrupted || !streamInterrupted.originalMessage) {
          console.warn('❌ [chatbotStore] No interrupted message to retry');
          return;
        }

        try {
          await withDeliveryAttempt(conversationId, async attempt => {
            console.log('🔄 [chatbotStore] Retrying interrupted message');
            addChatBreadcrumb('retry_interrupted', { conversationId, source: streamInterrupted.source });

            // Dismiss the toast
            toast.dismiss('stream-interrupted');

            // Set loading state immediately (like sendMessage does)
            setResponseStage(ResponseStage.WAITING, 'Leo is thinking...');

            // Clear the stream interrupted state and SSE connection state (reset retry counters)
            set({
              streamInterrupted: initialStreamInterruptedState,
              sseConnection: initialSSEConnectionState,
              error: null,
            });

            // Remove the partial assistant message if it exists
            const { messages } = get();
            const lastMessage = messages[messages.length - 1];
            if (lastMessage && lastMessage.type === MessageType.ASSISTANT) {
              // Remove the partial response message
              set({
                messages: messages.slice(0, -1),
              });
            }

            // Retry the message with isRetry=true to avoid duplicate user message
            await get()._handleRealResponse(
              streamInterrupted.originalMessage,
              attempt.requestId,
              conversationId,
              streamInterrupted.uploadedImages,
              true, // isRetry
              streamInterrupted.isHidden,
              streamInterrupted.displayText,
              attempt,
            );
          }, streamInterrupted.sendId);
        } catch (error) {
          console.error('❌ [chatbotStore] Retry failed:', error);
          // Preparation can fail after we cleared the old banner. Restore the
          // original retry inputs and leave WAITING even on repeated failures.
          set({ streamInterrupted, currentResponse: null, responseStage: ResponseStage.IDLE });
        }
      },

      recoverParentContinuation: async (turnId: string) => {
        const { conversationId } = get();
        if (!conversationId || !turnId.startsWith('continuation_')) return;
        const scopeGuard = WorkspaceContextService.captureScopeGuard();
        const isCurrentScope = () => scopeGuard() && get().conversationId === conversationId;
        // Both Resume and Retry for a module return converge here. These are
        // GET-only operations: a stopped continuation must never become a
        // synthetic human message or fall through to generic send recovery.
        get().clearStreamInterruptedState();
        await get().loadConversationById(conversationId, { background: true, isCurrentScope });
        if (!isCurrentScope()) return;
        const status = await NewConversationService.getStreamStatus(conversationId);
        if (!isCurrentScope()) return;
        if (status.isActive && status.turnId === turnId) {
          await get().attachToLiveStream(turnId, isCurrentScope);
          if (!isCurrentScope()) return;
        }
        if (!chatbotApi.hasActiveStream(conversationId)) {
          await get().loadConversationById(conversationId, { background: true, isCurrentScope });
          if (!isCurrentScope()) return;
        }
      },

      resumeStream: async () => {
        const { streamInterrupted, sseConnection, conversationId, currentResponse } = get();
        const recovery = continuationRecoveryContext(get().messages, streamInterrupted.turnId);
        if (recovery) {
          await get().recoverParentContinuation(recovery.turnId);
          return;
        }

        if (!conversationId) {
          console.warn('❌ [chatbotStore] No conversation ID for resume');
          // Fall back to retry
          await get().retryInterruptedMessage();
          return;
        }

        console.log('🔄 [chatbotStore] Attempting to resume stream from event:', sseConnection.lastEventId);

        // Dismiss toasts
        toast.dismiss('stream-interrupted');
        toast.dismiss('connection-lost');

        const lastAssistantContent = (() => {
          const { messages } = get();
          const lastAssistant = [...messages].reverse().find(m => m.type === MessageType.ASSISTANT);
          return typeof lastAssistant?.content === 'string' ? lastAssistant.content : '';
        })();
        const latestKnownPartial = [streamInterrupted.partialResponse, currentResponse?.streamedContent, lastAssistantContent]
          .filter((value): value is string => typeof value === 'string' && value.length > 0)
          .reduce((longest, value) => (value.length > longest.length ? value : longest), '');
        const resumePartial = latestKnownPartial;
        const resumeMessage = streamInterrupted.originalMessage || '';
        const resumeConversationId = conversationId;
        const resumeImages = streamInterrupted.uploadedImages;
        const resumeIsHidden = streamInterrupted.isHidden;
        const resumeDisplayText = streamInterrupted.displayText;
        // Every resume attempt, and the Retry it falls back to, is the same
        // typed message.
        const resumeSendId = streamInterrupted.sendId;
        let resumeLastContent = resumePartial;

        const upsertResumeMessage = (content: string, toolMarkers?: ToolMarker[], rewind = false) => {
          const result = upsertTurnReply(get().messages, content, toolMarkers, undefined, rewind);
          if (result.action === 'update') {
            set({ messages: result.messages });
          } else if (result.action === 'append') {
            get().addMessage({
              type: MessageType.ASSISTANT,
              content,
              status: MessageStatus.SENT,
              toolMarkers,
            });
          }
        };

        // Hide the banner while resuming, but keep state locally for fallback
        set({
          streamInterrupted: initialStreamInterruptedState,
        });

        // Set loading state
        set({
          responseStage: ResponseStage.WAITING,
          currentResponse: {
            requestId: `resume_${Date.now()}`,
            stage: ResponseStage.WAITING,
            progress: 50,
            startTime: Date.now(),
            estimatedDuration: 10000,
            currentActivity: 'Reconnecting...',
            canCancel: true,
            streamedContent: resumePartial || undefined,
          },
        });

        const attemptResume = async (attempt: number) => {
          console.log(`🔄 [chatbotStore] Resume attempt ${attempt} from event:`, sseConnection.lastEventId);

          // Try to reconnect to the SSE stream
          // The backend supports resuming via Last-Event-ID header
          const resumedResponse = await chatbotApi.sendMessageStream(
            resumeMessage || '', // Use empty string if no original message (pure resume)
            resumeConversationId,
            resumeImages,
            true, // isRetry
            sseConnection.lastEventId || undefined,
            resumePartial,
            resumeIsHidden,
            (progress: ResponseProgress) => {
              if (progress.isReplyReset) {
                // The answer so far is taken back; the working text before it stays.
                resumeLastContent = progress.streamedContent || '';
                upsertResumeMessage(resumeLastContent, progress.toolMarkers, true);
              } else if (progress.streamedContent && progress.streamedContent !== resumeLastContent) {
                resumeLastContent = progress.streamedContent;
                upsertResumeMessage(progress.streamedContent, progress.toolMarkers);
              }
              get().updateResponseProgress(progress);
            },
            resumeDisplayText,
            undefined,
            undefined,
            undefined,
            { attempt: createDeliveryAttempt(resumeConversationId, resumeSendId) },
          );

          if (resumedResponse?.content && typeof resumedResponse.content === 'string') {
            upsertResumeMessage(resumedResponse.content);
          }

          // If successful, clear the interrupted state
          clearInterruptionTimers();
          set({
            streamInterrupted: initialStreamInterruptedState,
            sseConnection: {
              ...get().sseConnection,
              isConnected: true,
              disconnectedAt: null,
              reconnectAttempts: 0,
            },
            responseStage: ResponseStage.IDLE,
            currentResponse: null,
          });

          console.log('✅ [chatbotStore] Resume successful');
        };

        let resumeSucceeded = false;
        let lastResumeError: unknown = null;

        const resumeRetryDelayMs = 1000;

        for (let attempt = 1; attempt <= 3; attempt++) {
          try {
            await attemptResume(attempt);
            resumeSucceeded = true;
            break;
          } catch (error) {
            lastResumeError = error;
            console.error(`❌ [chatbotStore] Resume attempt ${attempt} failed:`, error);
            if (attempt < 3) {
              await new Promise(resolve => setTimeout(resolve, resumeRetryDelayMs));
            }
          }
        }

        if (resumeSucceeded) {
          return;
        }

        console.error('❌ [chatbotStore] Resume failed after retries, falling back to retry:', lastResumeError);

        // Without the original message there is nothing to re-send — a retry
        // would POST an empty message, which the backend now refuses to enqueue
        // as a new turn. Settle to idle instead.
        if (!resumeMessage) {
          set({ responseStage: ResponseStage.IDLE, currentResponse: null });
          return;
        }

        // Resume failed - fall back to retry behavior
        toast.info('Could not resume. Restarting from your last message...', {
          autoClose: 3000,
          toastId: 'resume-fallback',
        });

        set({
          streamInterrupted: {
            ...initialStreamInterruptedState,
            isInterrupted: true,
            partialResponse: resumePartial,
            error: 'Resume failed. Retrying your message.',
            isRetryable: true,
            originalMessage: resumeMessage,
            uploadedImages: resumeImages || [],
            isHidden: resumeIsHidden,
            displayText: resumeDisplayText,
            conversationId: resumeConversationId || null,
            source: 'network',
            phase: 'retry',
            interruptedAt: Date.now(),
            sendId: resumeSendId,
          },
        });

        await get().retryInterruptedMessage();
      },

      clearStreamInterruptedState: () => {
        toast.dismiss('stream-interrupted');
        clearInterruptionTimers();
        set({
          streamInterrupted: initialStreamInterruptedState,
        });
      },

      // SSE connection tracking
      updateSSEConnectionState: (state: Partial<SSEConnectionState>) => {
        if (state.lastEventId !== undefined) {
          console.log('🧭 [chatbotStore] SSE lastEventId updated', { lastEventId: state.lastEventId });
        }
        set((prev) => ({
          sseConnection: {
            ...prev.sseConnection,
            ...state,
          },
        }));
      },

      updateStreamInterruptedState: (state: Partial<StreamInterruptedState>) => {
        set((prev) => ({
          streamInterrupted: {
            ...prev.streamInterrupted,
            ...state,
          },
        }));
        const next = { ...get().streamInterrupted, ...state };
        assertInterruptionState(next, 'updateStreamInterruptedState');
      },

      handleSSEDisconnect: (sendId?: string) => {
        const { sseConnection, responseStage, messages, conversationId } = get();
        const currentInterrupted = get().streamInterrupted;
        const continuationRecovery = continuationRecoveryContext(messages, sseConnection.turnId);

        // Only track disconnect if we were actively streaming
        if (responseStage !== ResponseStage.STREAMING && responseStage !== ResponseStage.WAITING) {
          return;
        }

        console.log('⚠️ [chatbotStore] SSE connection lost');

        // Find the last user message - this is what we'd retry
        const lastUserMessage = [...messages].reverse().find(m => m.type === MessageType.USER);
        const originalMessage = typeof lastUserMessage?.content === 'string'
          ? lastUserMessage.content
          : '';
        const originalUploadedImages = normalizeUploadedImages(lastUserMessage?.images);
        const originalIsHidden = lastUserMessage?.hidden;
        const originalDisplayText = lastUserMessage?.display;

        // Find partial response (last assistant message if streaming)
        const lastAssistantMessage = [...messages].reverse().find(m => m.type === MessageType.ASSISTANT);
        const partialResponse = responseStage === ResponseStage.STREAMING && lastAssistantMessage
          ? (typeof lastAssistantMessage.content === 'string' ? lastAssistantMessage.content : '')
          : '';

        const disconnectedAt = Date.now();

        clearInterruptionTimers();
        set({
          sseConnection: {
            ...sseConnection,
            isConnected: false,
            disconnectedAt,
            reconnectAttempts: sseConnection.reconnectAttempts + 1,
          },
        });

        if (currentInterrupted.source === 'llm' && currentInterrupted.isInterrupted) {
          console.log('🧭 [chatbotStore] Skipping network interruption due to LLM interruption', {
            phase: currentInterrupted.phase,
            error: currentInterrupted.error,
          });
          return;
        }

        const nextInterruptedState: StreamInterruptedState = {
          isInterrupted: false,
          turnId: sseConnection.turnId,
          partialResponse: continuationRecovery?.partialResponse ?? partialResponse,
          error: 'Connection lost - reconnecting',
          isRetryable: true,
          originalMessage: continuationRecovery?.originalMessage ?? originalMessage,
          uploadedImages: originalUploadedImages,
          isHidden: originalIsHidden,
          displayText: originalDisplayText,
          interruptedAt: null,
          conversationId: conversationId || null,
          source: 'network',
          phase: 'silent',
          disconnectStartedAt: disconnectedAt,
          lastReconnectAttemptAt: disconnectedAt,
          // Resume, and the Retry it falls back to, are the same typed message.
          sendId,
        };
        set({
          streamInterrupted: {
            ...nextInterruptedState,
          },
        });
        assertInterruptionState(nextInterruptedState, 'handleSSEDisconnect');
        scheduleNetworkPhaseUpdate(get, set, disconnectedAt);
      },

      handleSSEReconnect: () => {
        console.log('✅ [chatbotStore] SSE connection restored');

        toast.dismiss('connection-lost');
        clearInterruptionTimers();

        set({
          sseConnection: {
            ...get().sseConnection,
            isConnected: true,
            disconnectedAt: null,
            reconnectAttempts: 0,
          },
          streamInterrupted: initialStreamInterruptedState,
        });
      },
});

// The global, workspace-persisted chatbot singleton. Unchanged in behavior: the
// main chat reads this exactly as before. The only structural change is that the
// (set, get) initializer is now the shared `chatbotStateCreator` above.
export const useChatbotStore = create<ChatbotStore>()(
  persist(subscribeWithSelector(chatbotStateCreator), {
    name: STORE_NAME,
    storage: chatbotStoreStorage,
    partialize: (state) => ({
      state: state.state,
      conversationId: state.conversationId,
      slackSimulation: state.slackSimulation,
      panelStretched: state.panelStretched,
    }),
    onRehydrateStorage: () => (state) => {
      // Normalize transient states after hydration
      if (state) {
        const normalizedState = normalizeChatbotState(state.state);
        if (normalizedState !== state.state) {
          state.state = normalizedState;
        }
        // Update derived state based on persisted conversationId (per workspace)
        if (state.conversationId) {
          state.showWelcomeScreen = false;
          state.conversationLoading = true;
        } else {
          state.showWelcomeScreen = true;
          state.conversationLoading = false;
        }
      }
    },
  }),
);

/**
 * A `StoreApi<ChatbotStore>` — the shape shared by the global `useChatbotStore`
 * (a bound hook that also carries the vanilla `getState`/`setState`/`subscribe`
 * methods) and a `createChatbotStore()` factory instance (a plain vanilla store).
 * Code that only needs imperative access (services, effects) accepts this type so
 * it works against either.
 */
export type ChatbotStoreApi = ReturnType<typeof createChatbotStore>;

/**
 * Factory: an isolated, NON-persisted chatbot store for a single conversation
 * (e.g. an agentic board run streaming an async task into the live view). It
 * reuses the global store's entire state + action surface via the shared
 * `chatbotStateCreator`, so the same message / streaming / tool-marker rendering
 * components work against it unchanged — accessed in React via `ChatbotStoreProvider`
 * + `useChatbotStoreContext`. Persist is intentionally omitted: these stores are
 * ephemeral and must not collide with the global workspace-scoped localStorage key.
 */
export function createChatbotStore(conversationId?: string) {
  const store = createStore<ChatbotStore>()(subscribeWithSelector(chatbotStateCreator));
  if (conversationId) {
    store.setState({
      conversationId,
      state: ChatbotState.OPEN,
      showWelcomeScreen: false,
      conversationLoading: true,
    });
  }
  return store;
}

// Helper functions
function getProgressForStage(stage: ResponseStage): number {
  switch (stage) {
    case ResponseStage.IDLE: return 100;
    case ResponseStage.WAITING: return 25;
    case ResponseStage.STREAMING: return 75;
    case ResponseStage.ERROR: return 0;
    default: return 0;
  }
}

function getDefaultActivityForStage(stage: ResponseStage): string {
  switch (stage) {
    case ResponseStage.IDLE: return '';
    case ResponseStage.WAITING: return 'Leo is thinking...';
    case ResponseStage.STREAMING: return 'Streaming response...';
    case ResponseStage.ERROR: return 'Error occurred';
    default: return '';
  }
}

function getMockResponse(userMessage: string): string | any {
  const message = userMessage.toLowerCase();

  // Simulate approval response for delegation keywords
  if (message.includes('campaign') || message.includes('delegate') || message.includes('create') || message.includes('launch')) {
    return {
      requires_approval: true,
      response: "I need your approval to execute the following action(s):",
      approval_data: {
        company_name: "mock_company",
        conversation_id: "mock_conv_" + Date.now(),
        type: "write_tool_approval",
        pending_tools: [
          {
            tool_name: "delegate_task_to_experts",
            tool_call_id: "mock_fc_" + Date.now(),
            approval_text: "Create and launch a new Meta lead generation campaign. Budget: $3,000/month (~$100/day). Objective: Lead Generation with Meta lead forms. Structure: 1 CBO campaign with 2 ad sets - Cold Prospecting (interest targeting: Aviation, Airline pilot, Flight school) and Warm Retargeting (30-day website visitors). Targeting: Age 21-45, English, United States & Canada. Creative: Use existing high-performing assets plus create one new static image. Bid Strategy: Lowest Cost with bid cap $1.50 CPL. Success metrics: CPL ≤ $30, 12–15 leads weekly.",
            tool_args: {
              client_requirements_note: "Proceed with full build and launch as approved."
            }
          }
        ]
      }
    };
  }

  if (message.includes('performance') || message.includes('analytics')) {
    return "Here's your current performance overview:\n\n📊 **Key Metrics (Last 30 Days):**\n• Impressions: 125,000 (+15%)\n• Clicks: 3,400 (+22%)\n• CTR: 2.72% ↗️\n• CPC: $1.85 ↘️\n• Conversions: 89 (+31%)\n• ROAS: 4.2x ↗️\n\n🎯 **Top Opportunities:**\n• Mobile campaign optimization\n• Audience expansion potential\n• Creative refresh recommended\n\nWould you like detailed recommendations for any specific area?";
  }

  if (message.includes('competitor') || message.includes('analysis')) {
    return "**Competitive Analysis Summary:**\n\n🔍 **Market Position:**\nYou're currently performing well against key competitors in your space.\n\n📈 **Key Insights:**\n• Your CPC is 23% below industry average\n• Creative engagement rates are above median\n• Opportunity in video advertising (competitors heavy there)\n\n💡 **Recommendations:**\n• Consider expanding to YouTube advertising\n• Test competitor keyword targeting\n• Develop unique value proposition messaging\n\nShall I create a detailed competitive strategy?";
  }

  // Default response
  return "Thanks for your question! I'm here to help with your marketing campaigns, performance analysis, and strategic insights. I can assist with:\n\n• Campaign creation and optimization\n• Performance analysis and reporting\n• Competitive research\n• Audience targeting strategies\n• Creative recommendations\n\nWhat specific area would you like to explore?";
}

// Conversation ID is loaded directly in initialState

// Selectors for optimized subscriptions.
// Each entry is a hook factory: it's a plain function that only calls `useChatbotStore`
// (a hook) inside its body — the rule "hooks must be called from a component or another
// hook" is satisfied at the CALL SITE (a component invokes `useChatbotSelectors.foo()`),
// but the lint rule can't see through the object-shorthand indirection. Block-disable
// the rule for the map so every entry is covered, not just the first line.
/* eslint-disable react-hooks/rules-of-hooks */
export const useChatbotSelectors = {
  messageCapacity: () => useChatbotStore(state => state.messageCapacity),
  state: () => useChatbotStore(state => state.state),
  responseStage: () => useChatbotStore(state => state.responseStage),
  currentView: () => useChatbotStore(state => state.currentView),
  messages: () => useChatbotStore(state => state.messages),
  conversationId: () => useChatbotStore(state => state.conversationId),
  currentResponse: () => useChatbotStore(state => state.currentResponse),
  error: () => useChatbotStore(state => state.error),
  settings: () => useChatbotStore(state => state.settings),
  inputValue: () => useChatbotStore(state => state.inputValue),
  inputFocusRequestId: () => useChatbotStore(state => state.inputFocusRequestId),
  inputFocused: () => useChatbotStore(state => state.inputFocused),
  inputPlaceholderOverride: () => useChatbotStore(state => state.inputPlaceholderOverride),
  showWelcomeScreen: () => useChatbotStore(state => state.showWelcomeScreen),
  conversationHistory: () => useChatbotStore(state => state.conversationHistory),
  historyLoading: () => useChatbotStore(state => state.historyLoading),
  historySearchText: () => useChatbotStore(state => state.historySearchText),
  historyOriginFilter: () => useChatbotStore(state => state.historyOriginFilter),
  historyStartedByFilter: () => useChatbotStore(state => state.historyStartedByFilter),
  conversationLoading: () => useChatbotStore(state => state.conversationLoading),
  executionTodo: () => useChatbotStore(state => state.executionTodo),
  isAssetHubModalOpen: () => useChatbotStore(state => state.isAssetHubModalOpen),
  continuation: () => useChatbotStore(state => state.continuation),
  streamInterrupted: () => useChatbotStore(state => state.streamInterrupted),
  interruptAction: () => useChatbotStore(state => deriveInterruptAction(state.streamInterrupted)),
  sseConnection: () => useChatbotStore(state => state.sseConnection),
};
/* eslint-enable react-hooks/rules-of-hooks */

// Capture conversation_ended when the user leaves the page
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    _captureConversationEnded(useChatbotStore.getState().conversationId);
  });
}
